import { Component, OnInit, OnDestroy, AfterViewInit, ChangeDetectionStrategy, inject, ChangeDetectorRef, computed, signal, effect, untracked, ViewChild, viewChild, viewChildren, ElementRef, DestroyRef, afterNextRender, Injector } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { filter } from 'rxjs/operators';
import { AssistantContextService } from '@shared/services/assistant-context.service';
import { AssistantUiService } from '@core/assistant/assistant-ui.service';
import { AssistantActionSpec, AssistantFieldOption, AssistantFieldSpec } from '@core/assistant/assistant.types';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantAnchorDirective } from '@shared/directives/assistant-anchor.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { Signalr } from '@core/services/signalr';
import { descriptorEntriesToDict, valueEntriesToDict, dictToValueEntries } from '@shared/utils/openapi-schema-utils';
import { injectIsMobile } from '@shared/utils/viewport';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, merge } from 'rxjs';
import { switchMap } from 'rxjs/operators'; 
import { AgentsService } from '@generated/services/agents.service';
import { SpacesService } from '@generated/services/spaces.service';
import { ToolsService } from '@generated/services/tools.service';
import { ModelsService } from '@generated/services/models.service';
import { LanguagesService } from '@generated/services/languages.service';
import { AgentResult, UpdateAgentCommand, SpaceDto, GetToolsResult, AgentToolDto, ToolSummary, AgentSummary, AgentMode, TransferRuleDto } from '@generated/models';
import { GetModelsHierarchyResult, ModelTypeHierarchy, GatewayHierarchy, ModelHierarchy, GetLanguageLocalesResult, ModelVersionHierarchy } from '@generated/models';
import { AgentLanguageLocaleDto, DispatchAgentResult, LanguageLocaleDto } from '@generated/models';
import { environment } from '@env/environment';
import { NotificationService } from '@core/services/notification.service';
import { LayoutService } from '@core/services/layout.service';
import { AuthService } from '@core/services/auth.service';
import { LanguageService } from '@core/services/language.service';
import { WebhookToolComponent } from '@features/tools/webhook-tool/webhook-tool';
import { SystemToolComponent } from '@features/tools/system-tool/system-tool';
import { DeleteConfirmationComponent } from '@shared/components/delete-confirmation/delete-confirmation';
import { PreventOverlayCloseOnScrollDirective } from '@shared/directives/prevent-overlay-close-on-scroll';
import { VariableTrackerDirective } from '@shared/directives/variable-tracker';
import { PropertyDefinition } from '@shared/models/property-schema';
import { DynamicVariablesService } from '@core/services/dynamic-variables.service';
import { systemToolTitle, systemToolSummary } from '@shared/utils/system-tool-i18n';

// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { TextareaModule } from 'primeng/textarea';
import { SelectModule } from 'primeng/select';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { SliderModule } from 'primeng/slider';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { InputNumberModule } from 'primeng/inputnumber';
import { MenuModule } from 'primeng/menu';
import { SelectButtonModule } from 'primeng/selectbutton';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { DrawerModule } from 'primeng/drawer';
import { ChipModule } from 'primeng/chip';
import { MultiSelectModule } from 'primeng/multiselect';
import { TreeSelectModule } from 'primeng/treeselect';
import { ToolbarModule } from 'primeng/toolbar';
import { TabsModule, TabList } from 'primeng/tabs';
import { TooltipModule } from 'primeng/tooltip';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { DragDropModule } from 'primeng/dragdrop';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';

import { ModelSelector } from '@shared/components/model-selector/model-selector';
import { ModelSelectorButton } from '@shared/components/model-selector-button/model-selector-button';
import { TesterSelection } from '@shared/components/model-tester/model-tester.component';
import { AgentModelVersionDto } from '@generated/models/agent-model-version-dto';
import { AgentModelVersionEntryDto } from '@generated/models/agent-model-version-entry-dto';
import { AgentModelVersionEntryInputDto } from '@generated/models/agent-model-version-entry-input-dto';
import { OpenApiPropertyValueEntry } from '@generated/models/open-api-property-value-entry';
import { AgentTestsTabComponent } from '@features/agents/agent-tests-tab/agent-tests-tab.component';
import { AgentTestComponent } from '@features/agents/agent-test/agent-test';
import { PublicAgentDto } from '@generated/models/public-agent-dto';
import { PublishLanguageDto } from '@generated/models/publish-language-dto';
import { AGENT_FIELDS } from '@core/assistant/assistant-catalog';

/**
 * Системный инструмент с уже посчитанными локализованными строками —
 * шаблон берёт готовые значения вместо вызова функций локализации на
 * каждую интерполяцию (см. `systemTools`).
 */
interface LocalizedSystemTool {
  tool: ToolSummary;
  title: string;
  summary: string;
}

/** Зазор превью до края экрана и до панели кнопок. */
const PREVIEW_EDGE_GAP = 20;

/**
 * Инструменты, у которых есть список правил. У остальных системных тулов
 * настройка исчерпывается описанием и запретом прерываний.
 */
const RULE_TOOL_CODES = ['transfer-to-operator', 'transfer-to-agent', 'get-knowledge'];

@Component({
  selector: 'app-agent-edit',
  standalone: true,
  imports: [
    FormsModule,
    ButtonModule,
    InputTextModule,
    InputGroupModule,
    InputGroupAddonModule,
    TextareaModule,
    SelectModule,
    CardModule,
    ProgressSpinnerModule,
    SliderModule,
    ToggleButtonModule,
    ToggleSwitchModule,
    InputNumberModule,
    MenuModule,
    SelectButtonModule,
    BreadcrumbModule,
    DrawerModule,
    ChipModule,
    MultiSelectModule,
    TreeSelectModule,
    ToolbarModule,
    TabsModule,
    TooltipModule,
    TagModule,
    MessageModule,
    DragDropModule,
    WebhookToolComponent,
    SystemToolComponent,
    DeleteConfirmationComponent,
    PreventOverlayCloseOnScrollDirective,
    VariableTrackerDirective,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantAnchorDirective,
    AssistantActionDirective,
    ModelSelectorButton,
    AgentTestsTabComponent,
    AgentTestComponent
],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './agent-edit.html',
  styleUrl: './agent-edit.css',
  providers: [DialogService]
})
export class AgentEditComponent implements OnInit, OnDestroy, AfterViewInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = AGENT_FIELDS;

  private agentsService = inject(AgentsService);
  private spacesService = inject(SpacesService);
  private toolsService = inject(ToolsService);
  private modelsService = inject(ModelsService);
  private languagesService = inject(LanguagesService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private languageService = inject(LanguageService);
  private notificationService = inject(NotificationService);
  private assistantContext = inject(AssistantContextService);
  private assistantUi = inject(AssistantUiService);
  private signalr = inject(Signalr);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private injector = inject(Injector);
  private layoutService = inject(LayoutService);
  private authService = inject(AuthService);
  private dialogService = inject(DialogService);
  private dynamicVariablesService = inject(DynamicVariablesService);

  modelSelectorRef: DynamicDialogRef | undefined;

  readonly appName = environment.appName;
  readonly selectLlmModelLabel = $localize`:@@agentEdit.llm.select:Select LLM Model`;
  readonly selectTtsModelLabel = $localize`:@@agentEdit.tts.select:Select TTS Model`;
  readonly selectRealtimeModelLabel = $localize`:@@agentEdit.realtime.select:Select Realtime Model`;
  readonly selectRealtimeVoiceLabel = $localize`:@@agentEdit.realtime.voice.select:Выбрать голос`;
  readonly selectSttModelLabel = $localize`:@@agentEdit.stt.select:Select STT Model`;
  readonly selectVadModelLabel = $localize`:@@agentEdit.vad.select:Select VAD Model`;
  readonly placeholderTagLabel = $localize`:@@agentEdit.tool.placeholderTag:Placeholder`;
  readonly placeholderTooltip = $localize`:@@agentEdit.tool.placeholderTooltip:This is a placeholder tool that logs missing information without performing any action`;
  readonly copyToClipboardTooltip = $localize`:@@common.copyToClipboard:Copy to clipboard`;
  readonly savingLabel = $localize`:@@common.saving:Saving...`;
  readonly saveChangesLabel = $localize`:@@common.save:Save`;
  /** Полный и короткий лейблы кнопки запуска разговора. На мобиле
   *  «Talk To Agent» съедает всю нижнюю панель — там показываем «Talk».
   *  Тултип и aria-label остаются полными в обоих случаях. */
  readonly talkToAgentLabel = $localize`:@@agents.talk:Talk To Agent`;
  readonly talkShortLabel = $localize`:@@agents.talk.short:Talk`;
  private readonly isMobile = injectIsMobile();
  readonly talkButtonLabel = computed(() =>
    this.isMobile() ? this.talkShortLabel : this.talkToAgentLabel);
  readonly deleteAgentTitle = $localize`:@@agentEdit.delete.title:Delete Agent`;
  get deleteAgentSubtitle(): string {
    const name = this.agent()?.title || '';
    return $localize`:@@agentEdit.delete.subtitle:Are you sure you want to delete the agent "${name}:name:"? This action cannot be undone.`;
  }

  // Dynamic variables extracted from system prompt as PropertyDefinitions
  systemPromptVariables: PropertyDefinition[] = [];

  // Variable editor drawer state
  showVariableDrawer = false;
  editingVariable: PropertyDefinition | null = null;
  isNewVariable = false;

  // Computed property to track sidebar collapsed state
  sidebarCollapsed = computed(() => this.layoutService.sidebarCollapsed());

  readonly agent = signal<AgentResult | null>(null);
  spaces: SpaceDto[] = [];
  
  // Tools data
  readonly allTools = signal<GetToolsResult | null>(null);

  /** Подключённые к агенту инструменты. */
  readonly connectedTools = computed<ToolSummary[]>(() => {
    const tools = this.allTools()?.tools;
    const agentTools = this.agent()?.tools;
    if (!tools || !agentTools) return [];

    const connectedToolIds = agentTools.map(t => t.tool_id).filter(Boolean);
    return tools.filter(tool => connectedToolIds.includes(tool.id || ''));
  });

  /** Ещё не подключённые инструменты. */
  readonly availableTools = computed<ToolSummary[]>(() => {
    const tools = this.allTools()?.tools;
    if (!tools) return [];

    const connectedToolIds = this.agent()?.tools?.map(t => t.tool_id).filter(Boolean) || [];
    return tools.filter(tool => !connectedToolIds.includes(tool.id || ''));
  });

  /**
   * Системные инструменты с УЖЕ локализованными title/summary.
   *
   * Локализация вычисляется здесь, а не вызовом функции из шаблона:
   * интерполяция `{{ systemToolTitle(tool) }}` пересчитывалась только
   * когда до компонента доходил change detection, и до первого клика по
   * инструменту список оставался с исходными английскими строками.
   * Через computed значение становится частью графа сигналов и
   * перерисовывается сразу, как только приедут инструменты.
   *
   * Порядок — по локализованному title: иначе бэк тасует список между
   * запросами и юзер путается, где какой toggle.
   */
  readonly systemTools = computed<LocalizedSystemTool[]>(() => {
    const tools = this.allTools()?.tools;
    if (!tools) return [];

    return tools
      .filter(tool => tool.type === 'System')
      .map(tool => ({
        tool,
        title: systemToolTitle(tool),
        summary: systemToolSummary(tool),
      }))
      .sort((a, b) => a.title.localeCompare(b.title));
  });

  /** Локализованный title/summary для системного инструмента.
   *  Нужны точечно (шапка дравера), в списке используется systemTools(). */
  systemToolTitle(tool: ToolSummary): string { return systemToolTitle(tool); }
  systemToolSummary(tool: ToolSummary): string { return systemToolSummary(tool); }

  /** Webhook-инструменты. */
  readonly webhookTools = computed<ToolSummary[]>(() =>
    (this.allTools()?.tools ?? []).filter(tool => tool.type === 'Webhook')
  );

  /** Подключённые webhook-инструменты. */
  readonly connectedWebhookTools = computed<ToolSummary[]>(() =>
    this.connectedTools().filter(tool => tool.type === 'Webhook')
  );

  // Check if system tool is enabled
  isSystemToolEnabled(toolId: string): boolean {
    if (!this.agent()?.tools) return false;

    return this.agent()!.tools!.some(t => t.tool_id === toolId);
  }

  // Available agents data
  availableAgents: AgentSummary[] = [];
  isLoadingAgents: boolean = false;
  agentsLoaded: boolean = false;
  
  // Getter for filtered available webhook tools based on search
  get filteredAvailableWebhookTools(): ToolSummary[] {
    const tools = this.availableTools().filter(tool => tool.type === 'Webhook');
    if (!this.toolSearchQuery.trim()) return tools;

    const query = this.toolSearchQuery.toLowerCase();
    return tools.filter(tool =>
      tool.title?.toLowerCase().includes(query) ||
      tool.description?.toLowerCase().includes(query)
    );
  }
  
  // Getter to check if temperature control is allowed for current model
  get isTemperatureAllowed(): boolean {
    const primaryVersionId = this.primaryLlmVersionId;
    if (!primaryVersionId || !this.modelsHierarchy?.model_types) {
      return true; // Default to true if no model is selected or hierarchy is not loaded
    }

    // Find LLM model type
    const llmModelType = this.modelsHierarchy.model_types.find(mt => mt.code === 'llm');
    if (!llmModelType?.gateways) {
      return true;
    }

    // Find the model that contains the selected version
    for (const gateway of llmModelType.gateways) {
      if (!gateway.models) continue;

      for (const model of gateway.models) {
        if (!model.versions) continue;

        // Check if this model contains the selected version
        const hasVersion = model.versions.some((version: ModelVersionHierarchy) => version.id === primaryVersionId);
        if (hasVersion) {
          return model.allow_temperature !== false; // Allow by default if property is undefined
        }
      }
    }
    
    return true; // Default to true if model not found
  }
  
  // LLM model hierarchy data
  modelsHierarchy: GetModelsHierarchyResult | null = null;
  /** "Recommended only" / "all" filter for the model selector — tied to
   *  the Models hierarchy endpoint's includeAll query param. Default false
   *  matches the curated default surface; toggling reissues the request. */
  modelsIncludeAll = false;
  /** Идёт ли рефетч hierarchy — прокидываем в model-selector-button
   *  чтоб он показал спиннер поверх списка моделей. */
  readonly modelsHierarchyLoading = signal<boolean>(false);
  llmTreeNodes: any[] = [];
  selectedLlmModelVersion: any = null; // TreeSelect selected node object

  // TTS model hierarchy data
  ttsTreeNodes: any[] = [];
  selectedTtsModelVersion: any = null; // TreeSelect selected node object
  selectedVoiceLanguage: string = '';

  // Voice models per language
  primaryTtsModel: any = null;
  additionalTtsModels: { [languageCode: string]: any } = {};

  // STT model hierarchy data
  sttTreeNodes: any[] = [];
  selectedSttModelVersion: any = null; // TreeSelect selected node object for STT

  // Cached filtered TTS nodes per language
  filteredTtsNodes: { [languageCode: string]: any[] } = {};
  availableLanguagesMenuItems: any[] = [];

  // Helper methods for new language_locales structure
  get primaryLanguageLocale(): AgentLanguageLocaleDto | null {
    return this.agent()?.language_locales?.find(ll => ll.is_default) || null;
  }

  get additionalLanguageLocales(): AgentLanguageLocaleDto[] {
    return this.agent()?.language_locales?.filter(ll => !ll.is_default) || [];
  }

  // Get options for primary language locale
  get primaryLanguageLocaleOptions(): { label: string; value: string; system_prompt: string }[] {
    const primary = this.primaryLanguageLocale;
    if (!primary?.language_locale_id) return [];

    const languageLocale = this.languagesData?.language_locales?.find(
      ll => ll.id === primary.language_locale_id
    );

    if (!languageLocale?.options || languageLocale.options.length === 0) {
      return [];
    }

    return languageLocale.options
      .filter(option => option.id)
      .map(option => ({
        label: option.name || 'Unnamed Option',
        value: option.id!,
        system_prompt: option.system_prompt || ''
      }));
  }

  selectedSystemPromptOption: string | null = null;

  // Methods for template binding
  getPrimaryLanguage(): string | undefined {
    const primaryLocale = this.primaryLanguageLocale;
    if (!primaryLocale?.language_locale_id) return undefined;

    // Find locale by ID in languages data (flat structure)
    const ll = this.languagesData?.language_locales?.find(
      ll => ll.id === primaryLocale.language_locale_id
    );
    return ll?.full_code || undefined;
  }

  private get primaryLanguageLocaleEntry(): LanguageLocaleDto | undefined {
    const primary = this.primaryLanguageLocale;
    if (!primary?.language_locale_id) return undefined;
    return this.languagesData?.language_locales?.find(ll => ll.id === primary.language_locale_id);
  }

  get primaryLanguageId(): string | undefined {
    return this.primaryLanguageLocaleEntry?.language_id;
  }

  get primaryLocaleId(): string | undefined {
    return this.primaryLanguageLocaleEntry?.locale_id;
  }

  getAdditionalLanguages(): string[] {
    const additionalLocales = this.additionalLanguageLocales;
    const result: string[] = [];

    for (const agentLocale of additionalLocales) {
      if (!agentLocale.language_locale_id) continue;

      // Find locale by ID in languages data (flat structure)
      const ll = this.languagesData?.language_locales?.find(
        ll => ll.id === agentLocale.language_locale_id
      );
      if (ll?.full_code) {
        result.push(ll.full_code);
      }
    }
    return result;
  }

  getPrimaryFirstMessage(): string | undefined {
    return this.primaryLanguageLocale?.first_message || undefined;
  }

  setPrimaryFirstMessage(value: string | undefined): void {
    const primaryLocale = this.primaryLanguageLocale;
    if (primaryLocale) {
      primaryLocale.first_message = value || undefined;
    }
    // Update UI model
    this.languageModel.primaryFirstMessage = value || undefined;
  }

  getFirstMessageForLanguage(languageCode: string): string | undefined {
    const locale = this.findLocaleByFullCode(languageCode);
    if (!locale) return undefined;

    const agentLocale = this.agent()?.language_locales?.find(
      ll => ll.language_locale_id === locale.id && !ll.is_default
    );
    return agentLocale?.first_message || undefined;
  }

  setFirstMessageForLanguage(languageCode: string, value: string | undefined): void {
    const locale = this.findLocaleByFullCode(languageCode);
    if (!locale) return;

    const agentLocale = this.agent()?.language_locales?.find(
      ll => ll.language_locale_id === locale.id && !ll.is_default
    );
    if (agentLocale) {
      agentLocale.first_message = value || undefined;
    }
  }

  // Validation method to ensure at least one language has is_default: true
  private validateDefaultLanguage(): boolean {
    if (!this.agent()?.language_locales || this.agent()!.language_locales!.length === 0) {
      return false;
    }
    return this.agent()!.language_locales!.some(ll => ll.is_default === true);
  }

  // Helper method to find locale by full_code
  private findLocaleByFullCode(fullCode: string): LanguageLocaleDto | null {
    return this.languagesData?.language_locales?.find(
      ll => ll.full_code === fullCode
    ) || null;
  }

  // Helper method to find locale by id
  private findLocaleById(localeId: string): LanguageLocaleDto | null {
    return this.languagesData?.language_locales?.find(
      ll => ll.id === localeId
    ) || null;
  }

  // Get default first message for a language locale
  getDefaultFirstMessageForLocale(agentLocale: AgentLanguageLocaleDto): string {
    if (!agentLocale?.language_locale_id) {
      return 'Hello! How can I help you today?';
    }

    const locale = this.findLocaleById(agentLocale.language_locale_id);
    return locale?.default_first_message || 'Hello! How can I help you today?';
  }

  // Get default TTS version ID for a language locale
  getDefaultTtsVersionIdForLocale(agentLocale: AgentLanguageLocaleDto): string | undefined {
    if (!agentLocale?.language_locale_id) {
      return undefined;
    }

    const locale = this.findLocaleById(agentLocale.language_locale_id);
    return locale?.default_tts_version_id || undefined;
  }

  // Get effective TTS version ID for a language locale (user selection or default)
  getEffectiveTtsVersionIdForLocale(agentLocale: AgentLanguageLocaleDto): string | undefined {
    // If user has selected a TTS model, use it (per-locale TTS entry живёт
    // в agent.model_versions с agent_language_locale_id = locale.id).
    const ttsId = this.ttsEntryForLocale(agentLocale.id)?.model_version_id;
    if (ttsId) return ttsId;

    // Otherwise, use default from locale
    return this.getDefaultTtsVersionIdForLocale(agentLocale);
  }

  // Get TTS tree nodes for a specific agent locale
  getTtsTreeNodesForAgentLocale(agentLocale: AgentLanguageLocaleDto): any[] {
    const languageCode = this.getLanguageCodeForLocale(agentLocale);
    if (!languageCode) return [];
    return this.getTtsTreeNodesForLanguage(languageCode);
  }

  // Get selected TTS model node for agent locale
  getSelectedTtsModelForLocale(agentLocale: AgentLanguageLocaleDto): any {
    const effectiveTtsVersionId = this.getEffectiveTtsVersionIdForLocale(agentLocale);
    if (!effectiveTtsVersionId) return null;

    const languageCode = this.getLanguageCodeForLocale(agentLocale);
    if (!languageCode) return null;

    const ttsNodes = this.getTtsTreeNodesForLanguage(languageCode);

    // Find node by key recursively
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    return findNodeByKey(ttsNodes, effectiveTtsVersionId);
  }

  // Handle TTS model change for agent locale
  onAgentLocaleTtsModelChange(agentLocale: AgentLanguageLocaleDto, selectedNode: any): void {
    if (!agentLocale.id) return;
    this.upsertTtsForLocale(agentLocale.id, { model_version_id: selectedNode?.key || undefined });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  // Get speech rate for locale (default 1.0)
  getSpeechRateForLocale(agentLocale: AgentLanguageLocaleDto): number {
    return agentLocale.speed ?? 1.0;
  }

  // Set speech rate for locale
  setSpeechRateForLocale(agentLocale: AgentLanguageLocaleDto, rate: number): void {
    agentLocale.speed = rate;
    this.cdr.markForCheck();
  }

  // Background sound type options
  backgroundSoundTypeOptions = [
    { label: $localize`:@@agentEdit.bgSound.option.none:None`, value: null },
    { label: $localize`:@@agentEdit.bgSound.option.office:Office`, value: 'Office' }
  ];

  // Get background sound type for locale
  getBackgroundSoundTypeForLocale(agentLocale: AgentLanguageLocaleDto): string | null {
    return agentLocale.background_sound_type ?? null;
  }

  // Set background sound type for locale
  setBackgroundSoundTypeForLocale(agentLocale: AgentLanguageLocaleDto, type: string | null): void {
    agentLocale.background_sound_type = type as any;
    this.cdr.markForCheck();
  }

  // Get background volume for locale (default 50%, stored as 0-1)
  getBackgroundVolumeForLocale(agentLocale: AgentLanguageLocaleDto): number {
    // Если тип звука выбран, но громкость null - инициализируем дефолтным
    if (agentLocale.background_sound_type && (agentLocale.background_sound_volume === null || agentLocale.background_sound_volume === undefined)) {
      agentLocale.background_sound_volume = 0.5;
    }
    const volume = agentLocale.background_sound_volume ?? 0.5;
    return Math.round(volume * 100);
  }

  // Set background volume for locale (converts from 0-100% to 0-1)
  setBackgroundVolumeForLocale(agentLocale: AgentLanguageLocaleDto, volumePercent: number): void {
    agentLocale.background_sound_volume = volumePercent / 100;
    this.cdr.markForCheck();
  }


  // Widget appearance options
  widgetVariantOptions = [
    { label: $localize`:@@agentEdit.variant.tiny:Tiny`, value: 'Tiny' },
    { label: $localize`:@@agentEdit.variant.compact:Compact`, value: 'Compact' },
    { label: $localize`:@@agentEdit.variant.full:Full`, value: 'Full' }
  ];

  // Widget placement options
  widgetPlacementOptions = [
    { label: $localize`:@@agentEdit.placement.topLeft:Top-left`, value: 'TopLeft' },
    { label: $localize`:@@agentEdit.placement.top:Top`, value: 'Top' },
    { label: $localize`:@@agentEdit.placement.topRight:Top-right`, value: 'TopRight' },
    { label: $localize`:@@agentEdit.placement.bottomLeft:Bottom-left`, value: 'BottomLeft' },
    { label: $localize`:@@agentEdit.placement.bottom:Bottom`, value: 'Bottom' },
    { label: $localize`:@@agentEdit.placement.bottomRight:Bottom-right`, value: 'BottomRight' }
  ];

  // Widget theme options — привязаны напрямую к publish_options.theme
  // (реальное бэковское поле, WidgetTheme = 'Light'|'Dark'|'Auto').
  widgetThemeOptions = [
    { label: $localize`:@@agentEdit.theme.light:Light`, value: 'Light' },
    { label: $localize`:@@agentEdit.theme.dark:Dark`, value: 'Dark' },
    { label: $localize`:@@agentEdit.theme.auto:Auto (system)`, value: 'Auto' },
  ];

  // Delete confirmation dialog
  showDeleteConfirmation = false;
  isDeletingAgent = false;

  // Sync UI model with language_locales
  private syncLanguageModel(): void {
    if (!this.agent()?.language_locales) {
      this.languageModel = {
        primaryLanguage: undefined,
        additionalLanguages: [],
        primaryFirstMessage: undefined
      };
      return;
    }

    const primaryLocale = this.agent()!.language_locales!.find(ll => ll.is_default);
    const additionalLocales = this.agent()!.language_locales!.filter(ll => !ll.is_default);

    // Set primary language (flat structure)
    this.languageModel.primaryLanguage = undefined;
    if (primaryLocale?.language_locale_id) {
      const ll = this.languagesData?.language_locales?.find(
        ll => ll.id === primaryLocale.language_locale_id
      );
      if (ll) {
        this.languageModel.primaryLanguage = ll.full_code || undefined;
      }
    }

    // Set additional languages (flat structure)
    this.languageModel.additionalLanguages = [];
    for (const agentLocale of additionalLocales) {
      if (!agentLocale.language_locale_id) continue;
      const ll = this.languagesData?.language_locales?.find(
        ll => ll.id === agentLocale.language_locale_id
      );
      if (ll?.full_code) {
        this.languageModel.additionalLanguages.push(ll.full_code);
      }
    }

    // Set primary first message
    this.languageModel.primaryFirstMessage = primaryLocale?.first_message || undefined;

    // Primary language TTS nodes are now handled by getPrimaryLanguageTtsNodes() method
  }

  // Compatibility getters for old code (temporary)
  get agent_language(): string | undefined {
    const primaryLocale = this.primaryLanguageLocale;
    if (!primaryLocale?.language_locale_id) return undefined;

    // Find locale by ID in languages data (flat structure)
    const ll = this.languagesData?.language_locales?.find(
      ll => ll.id === primaryLocale.language_locale_id
    );
    return ll?.full_code || undefined;
  }

  get additional_languages(): string[] {
    const additionalLocales = this.additionalLanguageLocales;
    const result: string[] = [];

    for (const agentLocale of additionalLocales) {
      if (!agentLocale.language_locale_id) continue;

      // Find locale by ID in languages data (flat structure)
      const ll = this.languagesData?.language_locales?.find(
        ll => ll.id === agentLocale.language_locale_id
      );
      if (ll?.full_code) {
        result.push(ll.full_code);
      }
    }
    return result;
  }

  get tts_version_id(): string | undefined {
    const primary = this.primaryLanguageLocale;
    if (!primary?.id) return undefined;
    return this.ttsEntryForLocale(primary.id)?.model_version_id;
  }

  get first_message(): string | undefined {
    return this.primaryLanguageLocale?.first_message || undefined;
  }
  
  // Tools dropdown state
  showToolsDropdown: boolean = false;
  toolSearchQuery: string = '';
  
  // Webhook drawer state
  showWebhookDrawer: boolean = false;
  selectedToolId: string | null = null;

  // System tool drawer state
  showSystemToolDrawer: boolean = false;
  selectedSystemTool: ToolSummary | null = null;
  
  // STT будет реализован позже через Models hierarchy или отдельный сервис
  
  
  // Temperature preset buttons state
  temperaturePresets = {
    deterministic: { label: 'Deterministic', value: 0, active: false },
    creative: { label: 'Creative', value: 0.5, active: false },
    moreCreative: { label: 'More Creative', value: 1, active: false }
  };
  
  // Breadcrumb configuration
  breadcrumbItems: any[] = [];

  // Languages data from API
  languagesData: GetLanguageLocalesResult | null = null;
  languageOptions: any[] = [];

  // Simple UI model for languages - synced with language_locales
  languageModel = {
    primaryLanguage: undefined as string | undefined,
    additionalLanguages: [] as string[],
    primaryFirstMessage: undefined as string | undefined
  };

  // TTS nodes are now handled by methods, not cached properties

  // Language locale methods
  getLanguageOptionsForLocale(locale: AgentLanguageLocaleDto): { label: string; value: string; languageCode: string; localeName?: string | null; nativeLocaleName?: string | null; flag?: string | null; isDefault?: boolean }[] {
    if (locale.is_default) {
      // For default locale, show all languages
      return this.languageOptions;
    } else {
      // For additional locales, show languages not already used (flat structure)
      const usedLanguageIds = (this.agent()?.language_locales || [])
        .map(ll => ll.language_locale_id)
        .filter(id => id !== locale.language_locale_id);

      return this.languageOptions.filter(option =>
        !usedLanguageIds.some(usedId => {
          // Find locale by ID and compare full_code
          const ll = this.languagesData?.language_locales?.find(
            ll => ll.id === usedId && ll.full_code === option.value
          );
          return !!ll;
        })
      );
    }
  }

  getLanguageCodeForLocale(locale: AgentLanguageLocaleDto): string | undefined {
    if (!locale.language_locale_id) return undefined;

    const ll = this.languagesData?.language_locales?.find(
      ll => ll.id === locale.language_locale_id
    );
    return ll?.full_code || undefined;
  }

  onLocaleLanguageChange(index: number, languageCode: string): void {
    if (!this.agent()?.language_locales || !this.agent()!.language_locales![index]) return;

    // Find locale by language code (flat structure)
    const ll = this.languagesData?.language_locales?.find(
      ll => ll.full_code === languageCode
    );

    if (ll) {
      const newLocaleId = ll.id;
      const currentLocale = this.agent()!.language_locales![index];

      // If changing default language, remove duplicates from additional languages
      if (currentLocale.is_default) {
        // Remove any additional language with the same locale_id
        this.agent()!.language_locales = this.agent()!.language_locales!.filter((l, i) =>
          i === index || l.language_locale_id !== newLocaleId
        );
      }

      // Update the current locale + seed default TTS entry for it
      currentLocale.language_locale_id = newLocaleId;
      if (currentLocale.id && ll.default_tts_version_id) {
        this.upsertTtsForLocale(currentLocale.id, { model_version_id: ll.default_tts_version_id });
      }

      this.onLanguageChange();
      this.updateAvailableLanguagesMenu();
    }
  }

  onLocaleFirstMessageChange(index: number, message: string): void {
    if (!this.agent()?.language_locales || !this.agent()!.language_locales![index]) return;

    this.agent()!.language_locales![index].first_message = message;

    // Extract and merge variables from first message with existing schema
    this.updateVariablesFromAllSources();

    this.onLanguageChange();
  }

  // Update variables from all text sources (system prompt + all first messages)
  private updateVariablesFromAllSources(): void {
    if (!this.agent()) return;

    // Collect all texts with variables
    const texts: string[] = [];

    // Add system prompt
    if (this.agent()!.system_prompt) {
      texts.push(this.agent()!.system_prompt!);
    }

    // Add all first messages
    if (this.agent()!.language_locales) {
      this.agent()!.language_locales!.forEach(locale => {
        if (locale.first_message) {
          texts.push(locale.first_message);
        }
      });
    }

    // Combine all texts and extract variables
    const combinedText = texts.join(' ');
    const allVariables = this.dynamicVariablesService.updateVariablesFromText(
      combinedText,
      this.agent()!.variables_open_api_schema
    );

    // Update schema
    const newSchema = this.dynamicVariablesService.propertiesToSchema(allVariables);
    this.agent.set({ ...this.agent()!, variables_open_api_schema: newSchema });
    this.cdr.markForCheck();
  }

  removeLanguageLocale(index: number): void {
    if (!this.agent()?.language_locales) return;

    this.agent()!.language_locales!.splice(index, 1);
    this.onLanguageChange();
    this.updateAvailableLanguagesMenu();
  }

  addLanguageLocale(languageCode: string): void {
    if (!this.agent()?.language_locales) {
      this.agent()!.language_locales = [];
    }

    // Find locale by language code (flat structure)
    const ll = this.languagesData?.language_locales?.find(
      ll => ll.full_code === languageCode
    );

    if (ll) {
      const newLocale: AgentLanguageLocaleDto = {
        language_locale_id: ll.id,
        is_default: false,
        first_message: undefined,
      };
      this.agent()!.language_locales!.push(newLocale);
      // Default TTS entry для новой локали. Backend прицепит её через
      // agent_language_locale_id = локали.id после того как ту засоздаст
      // (пока newLocale.id undefined — TTS-entry привязать некуда).
      // При первом save бэк вернёт свежую локаль с id, а defaults
      // застэят на следующем ре-render.
      this.onLanguageChange();
      this.updateAvailableLanguagesMenu();
    }
  }

  updateAvailableLanguagesMenu(): void {
    const usedLanguageIds = (this.agent()?.language_locales || [])
      .map(ll => ll.language_locale_id);

    const availableOptions = this.languageOptions.filter(option =>
      !usedLanguageIds.some(usedId => {
        // Find locale by ID and compare full_code (flat structure)
        const ll = this.languagesData?.language_locales?.find(
          ll => ll.id === usedId && ll.full_code === option.value
        );
        return !!ll;
      })
    );

    this.availableLanguagesMenuItems = availableOptions.map(option => ({
      label: option.label,
      command: () => this.addLanguageLocale(option.value)
    }));
  }

  private sortLanguageLocales(): void {
    if (!this.agent()?.language_locales) return;

    // Sort so is_default is always first
    this.agent()!.language_locales!.sort((a, b) => {
      if (a.is_default && !b.is_default) return -1;
      if (!a.is_default && b.is_default) return 1;
      return 0;
    });
  }

  // Убираем сложное отслеживание изменений - отправляем всю модель

  // Primary language options (all languages available)
  getPrimaryLanguageOptions() {
    return this.languageOptions;
  }

  // Filtered options for additional languages (exclude primary language)
  getAdditionalLanguageOptions() {
    const primaryLang = this.languageModel.primaryLanguage;
    if (!primaryLang) return this.languageOptions;
    return this.languageOptions.filter(option => option.value !== primaryLang);
  }

  // Voice language options (all agent languages: primary + additional)
  getVoiceLanguageOptions() {
    if (!this.agent()) return [];

    const options = [];

    // Add primary language (now using full_code)
    const primaryLang = this.getPrimaryLanguage();
    if (primaryLang) {
      const primaryOption = this.languageOptions.find(opt => opt.value === primaryLang);
      if (primaryOption) {
        options.push(primaryOption);
      }
    }

    // Add additional languages (now using full_code)
    const additionalLangs = this.getAdditionalLanguages();
    for (const langFullCode of additionalLangs) {
      const lang = this.languageOptions.find(opt => opt.value === langFullCode);
      if (lang) {
        options.push(lang);
      }
    }

    return options;
  }

  // When primary language changes (now using full_code)
  onPrimaryLanguageChange(newLanguage: string): void {
    if (!this.agent()) return;

    // Initialize language_locales if not exists
    if (!this.agent()!.language_locales) {
      this.agent()!.language_locales = [];
    }

    const locale = this.findLocaleByFullCode(newLanguage);
    if (!locale) return;

    const locales = this.agent()!.language_locales!;
    const primaryIndex = locales.findIndex(ll => ll.is_default);

    if (primaryIndex >= 0) {
      // Update existing primary language
      locales[primaryIndex].language_locale_id = locale.id!;
      if (locales[primaryIndex].id && locale.default_tts_version_id) {
        this.upsertTtsForLocale(locales[primaryIndex].id!, { model_version_id: locale.default_tts_version_id });
      }
    } else {
      // Create new primary language (TTS entry прицепится после save
      // когда бэк вернёт локаль с id).
      locales.unshift({
        language_locale_id: locale.id!,
        is_default: true,
        first_message: undefined,
      });
    }

    // Remove new primary language from additional languages
    for (let i = locales.length - 1; i >= 0; i--) {
      const ll = locales[i];
      if (!ll.is_default && ll.language_locale_id === locale.id) {
        locales.splice(i, 1);
      }
    }

    this.syncLanguageModel();
    this.cdr.markForCheck();
  }

  // When additional languages change (now using full_code)
  onAdditionalLanguagesChange(newLanguages: string[]): void {
    if (!this.agent()) return;

    // Initialize language_locales if not exists
    if (!this.agent()!.language_locales) {
      this.agent()!.language_locales = [];
    }

    const locales = this.agent()!.language_locales!;

    // Remove all non-primary languages
    for (let i = locales.length - 1; i >= 0; i--) {
      if (!locales[i].is_default) {
        locales.splice(i, 1);
      }
    }

    // Add new additional languages (TTS entries прицепятся после save)
    for (const fullCode of newLanguages) {
      const locale = this.findLocaleByFullCode(fullCode);
      if (locale) {
        const isPrimary = locales.some(ll => ll.is_default && ll.language_locale_id === locale.id);
        if (!isPrimary) {
          locales.push({
            language_locale_id: locale.id!,
            is_default: false,
            first_message: undefined,
          });
        }
      }
    }

    this.syncLanguageModel();
    this.cdr.markForCheck();
  }

  // Check if system tool is disabled
  isSystemToolDisabled(toolCode?: string | null): boolean {
    const disabledTools: string[] = [];
    return disabledTools.includes(toolCode || '');
  }

  // Loading states
  isSaving = false;

  // Active tab management. Значения:
  //   'settings' — длинная страница с секциями Agent/Voice/Transcriber/VAD/Widget,
  //                конкретная секция в фокусе трекается scrollspy'ем ниже.
  //   'tests'    — отдельный режим (app-agent-tests-tab), длинная страница скрыта.
  //   'talk'     — отдельный режим (app-agent-test), длинная страница скрыта.
  readonly activeTabIndex = signal<'settings' | 'tests' | 'talk'>('settings');
  /**
   * Ключ секции которая сейчас в области viewport'а — обновляется
   * IntersectionObserver'ом при скролле. Nav подсвечивает соответствующий
   * пункт. Значения: 'agent' | 'voice' | 'transcriber' | 'vad' | 'widget'.
   */
  readonly currentSection = signal<string>('sec-agent');

  /**
   * Инстансы редакторов инструментов внутри дравер'ов.
   *
   * Кнопки «Назад»/«Сохранить» живут в футере самого p-drawer
   * (`pTemplate="footer"` — тот же приём, что в модел-тестере: футер
   * лежит вне скроллируемого контента, поэтому закреплён снизу штатно,
   * без sticky/fixed). Футер — сестринский к компоненту узел, дотянуться
   * до него template-переменной нельзя, поэтому дёргаем методы через
   * query.
   */
  readonly systemToolCmp = viewChild(SystemToolComponent);
  readonly webhookToolCmp = viewChild(WebhookToolComponent);

  /**
   * Показывается ли сейчас плавающее превью виджета.
   *
   * Оно висит `position: fixed` поверх формы, поэтому на мобиле закрывает
   * нижние поля секции «Виджет» и до них невозможно долистать. По этому
   * флагу секция получает запас снизу (см. `.with-widget-preview`).
   */
  readonly showWidgetPreview = computed<boolean>(() =>
    !!this.agent()?.is_published
    && this.activeTabIndex() === 'settings'
    && this.currentSection() === 'sec-widget'
  );

  /** Элементы, по которым считаем, влезает ли превью справа. */
  private readonly previewEl = viewChild<ElementRef<HTMLElement>>('widgetPreview');
  private readonly actionsEl = viewChild<ElementRef<HTMLElement>>('actionButtons');

  /**
   * Помещается ли превью виджета СПРАВА от панели действий.
   *
   * Панель `.action-buttons` — 800px по центру контентной области, а
   * превью висит `position: fixed` у правого края. Когда окно узкое,
   * правый «карман» оказывается уже виджета, и он наезжает на кнопки
   * «Сохранить» / «Поговорить» — по ним невозможно попасть.
   *
   * true  → оставляем как есть, превью стоит сбоку и никому не мешает.
   * false → поднимаем его НАД панелью (.is-above-actions) и резервируем
   *         под него место в конце секции, чтобы оно не накрывало
   *         последние поля (см. --sn-preview-height).
   */
  readonly previewFitsAside = signal(true);

  /** Высота превью — уходит в CSS, чтобы зарезервировать место в потоке. */
  private readonly previewHeight = signal(0);

  /** Та же высота, готовой CSS-длиной: кастомные свойства Angular
   *  прокидывает строкой, суффикс `.px` для них не работает. */
  readonly previewHeightPx = computed(() => `${this.previewHeight()}px`);


  /**
   * Значение для `<p-tabs [value]>`: в режиме settings — id текущей секции
   * (обновляется scrollspy'ем IntersectionObserver'а ниже), в tests/talk —
   * метка режима. PrimeNG сам подсвечивает соответствующий таб.
   */
  readonly tabsValue = computed<string>(() => {
    const mode = this.activeTabIndex();
    return mode === 'settings' ? this.currentSection() : mode;
  });

  /** Клик по табу: tests/talk — переключение режима, остальное —
   *  smooth-scroll к соответствующей секции. */
  onTabsClick(value: string | number | undefined): void {
    if (typeof value !== 'string') return;
    if (value === 'tests' || value === 'talk') {
      this.setActiveTab(value);
    } else {
      this.scrollToSection(value);
    }
  }

  /** Scroll spy: слушатели устанавливаются на реальный scroll-контейнер
   *  (.main-content) и на window (fallback). При каждом scroll'е rAF-throttl'им
   *  и пересчитываем какая секция сейчас в фокусе — та, у которой верх
   *  ближе всего к активной зоне (120px от верха контейнера) сверху.
   *
   *  Почему НЕ IntersectionObserver: rootMargin / порядок callback'а /
   *  timing регистрации давали ложноположительные и/или молчаливые
   *  отсутствия срабатываний в разных браузерах — приходилось гадать.
   *  Scroll listener + getBoundingClientRect — прямолинейно и всегда
   *  работает. */
  private scrollSpyContainer: HTMLElement | null = null;
  private scrollSpyHandler: (() => void) | null = null;
  private scrollSpyRafId: number | null = null;

  /**
   * Разделы длинной страницы настроек. Порядок = порядок секций в DOM
   * и порядок пунктов в nav'е.
   */
  readonly settingsSections: Array<{ id: string; labelKey: string; label: string }> = [
    { id: 'sec-agent',       labelKey: '@@agentEdit.tab.agent',              label: 'Agent' },
    { id: 'sec-voice',       labelKey: '@@agentEdit.tab.voice',              label: 'Voice' },
    { id: 'sec-transcriber', labelKey: '@@agentEdit.tab.transcriber',        label: 'Transcriber' },
    { id: 'sec-vad',         labelKey: '@@agentEdit.tab.voiceAutoDetection', label: 'Voice auto detection' },
    { id: 'sec-widget',      labelKey: '@@agentEdit.tab.widget',             label: 'Widget' },
  ];

  scrollToSection(sectionId: string): void {
    // Обновляем currentSection сразу — иначе effect в constructor'е
    // подхватит только после IntersectionObserver'а и fragment на короткое
    // время будет отставать. activeTabIndex.set('settings') важен, если
    // до клика мы были в tests/talk-режиме.
    const wasSwitching = this.activeTabIndex() !== 'settings';
    this.activeTabIndex.set('settings');
    this.currentSection.set(sectionId);
    // При смене режима (talk/tests → settings) Angular нужно два CD tick'а
    // чтобы отрендерить `@if (activeTabIndex() === 'settings')` блок с
    // секциями. queueMicrotask'а недостаточно — секции ещё не в DOM,
    // getElementById вернёт null, скролл не сработает. requestAnimationFrame
    // ждёт следующий paint — после этого DOM обновлён.
    const doScroll = () => {
      const el = document.getElementById(sectionId);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };
    if (wasSwitching) {
      requestAnimationFrame(() => requestAnimationFrame(doScroll));
    } else {
      queueMicrotask(doScroll);
    }
  }

  /**
   * Пишет фрагмент в URL, чтобы можно было поделиться ссылкой на конкретную
   * секцию (`/agents/:id#sec-widget`) или обновить страницу и не потерять
   * место. Используем replaceUrl, чтобы клики по табам не засоряли историю
   * браузера. */
  private syncUrlFragment(fragment: string): void {
    this.router.navigate([], {
      relativeTo: this.route,
      fragment,
      replaceUrl: true,
      queryParamsHandling: 'preserve',
    });
  }

  /** Запускается из ngAfterViewInit / effect1 после mount'а секций.
   *  Вешает scroll-listener на `.main-content` (реальный scroll-контейнер
   *  layout'а) + window (fallback если layout меняется). На каждом
   *  scroll'е rAF-throttl'им и через `getBoundingClientRect` определяем,
   *  какая секция сейчас в фокусе — та, чей `top` находится максимально
   *  близко к 120px от верха, но ещё не проехала.
   *
   *  Почему НЕ IntersectionObserver: rootMargin/threshold/timing давали
   *  ложноположительные и «молчаливые» отсутствия срабатываний —
   *  прямой rect на scroll всегда работает и его легко отладить. */
  private setupScrollSpy(): void {
    this.teardownScrollSpy();
    const container =
      (document.querySelector('.main-content') as HTMLElement | null) ?? null;
    this.scrollSpyContainer = container;
    // ~120px sticky-nav'а сверху + мелкий запас. Секция считается активной
    // если её top прошёл эту границу (top <= threshold), но следующая
    // ещё нет.
    const THRESHOLD_PX = 120;
    const compute = () => {
      this.scrollSpyRafId = null;
      let activeId: string | null = null;
      for (const s of this.settingsSections) {
        const el = document.getElementById(s.id);
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top - THRESHOLD_PX <= 0) {
          // Секция уже переехала границу — она (или следующая) активна.
          // Ищем самую нижнюю такую = последнюю в порядке секций.
          activeId = s.id;
        } else {
          break;
        }
      }
      // Fallback: если ничего не переехало границу (мы выше всех),
      // держим первую секцию.
      if (!activeId) activeId = this.settingsSections[0]?.id ?? null;
      if (activeId && activeId !== this.currentSection()) {
        this.currentSection.set(activeId);
      }
    };
    const onScroll = () => {
      if (this.scrollSpyRafId !== null) return;
      this.scrollSpyRafId = requestAnimationFrame(compute);
    };
    this.scrollSpyHandler = onScroll;
    // Слушаем оба: main-content (реальный scroll-контейнер) и window
    // (edge-case, когда layout меняется или scroll вешают на document).
    container?.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    // Первый прогон — синхронизировать nav с текущим положением.
    compute();
  }

  private teardownScrollSpy(): void {
    if (this.scrollSpyHandler) {
      this.scrollSpyContainer?.removeEventListener('scroll', this.scrollSpyHandler);
      window.removeEventListener('scroll', this.scrollSpyHandler);
    }
    if (this.scrollSpyRafId !== null) {
      cancelAnimationFrame(this.scrollSpyRafId);
      this.scrollSpyRafId = null;
    }
    this.scrollSpyHandler = null;
    this.scrollSpyContainer = null;
  }

  /**
   * Live-snapshot PublicAgentDto для встроенного <app-agent-test> внутри
   * вкладки Talk to agent. AgentTestComponent, получив publicAgentOverride,
   * НЕ идёт за /public — вместо этого читает языки и publish_options из
   * override'а. Значит правки в редакторе (список language_locales,
   * default-локаль, appearance/placement/labels) отражаются в тестере
   * без save.
   *
   * Что live: languages (флаг+имя+is_default) и publish_options.
   * Что НЕ live: first_message / LLM / STT / TTS / инструкции / tools —
   * бэк дёргает их из БД при dispatch'е, тестер их не диктует. Для их
   * тестирования требуется сохранение.
   */
  readonly publicAgentPreview = computed<PublicAgentDto | null>(() => {
    const a = this.agent();
    if (!a) return null;
    const publishOptions = a.public_agent?.publish_options;
    const languages: PublishLanguageDto[] = (a.language_locales ?? [])
      .map((locale) => {
        const localeId = locale.language_locale_id;
        if (!localeId) return null;
        const ll = this.languagesData?.language_locales?.find((l) => l.id === localeId);
        return {
          language_locale_id: localeId,
          flag: ll?.locale_flag ?? '',
          language: ll?.language_name ?? localeId,
          language_code: ll?.language_code ?? '',
          locale: ll?.locale_name ?? '',
          locale_code: ll?.locale_code ?? '',
          full_code: ll?.full_code ?? '',
          is_default: locale.is_default ?? false,
        } as PublishLanguageDto;
      })
      .filter((x): x is PublishLanguageDto => x !== null);
    return {
      languages,
      publish_options: publishOptions ? { ...publishOptions } : null,
      variables_open_api_schema: a.variables_open_api_schema ?? null,
    };
  });

  /** Флаг: deep-link из URL fragment уже был применён после загрузки агента.
   *  Не даём effect'у ниже повторно скроллить при последующих мутациях агента. */
  private deepLinkApplied = false;
  /** Fragment из URL, зафиксированный в конструкторе — потому что уже в
   *  первый CD-tick effect2 (write-side) пишет initial ('sec-agent') и
   *  перетирает оригинальное значение до того как effect1 успеет его
   *  прочитать. Читаем один раз, храним, потом применяем. */
  private readonly initialFragment: string | null = this.route.snapshot.fragment;

  constructor() {
    // Превью появилось/исчезло или сменился appearance — перемерить.
    // ResizeObserver на самом превью ловит смену appearance (Tiny/Compact/
    // Full меняют и ширину, и высоту), на панели кнопок — сворачивание
    // сайдбара и перетаскивание панели агента: и то и другое меняет её
    // ширину. Плюс resize окна, который ни один из них не покрывает,
    // когда размеры элементов остались прежними.
    effect((onCleanup) => {
      const preview = this.previewEl()?.nativeElement;
      const actions = this.actionsEl()?.nativeElement;
      if (!preview || !actions) return;

      const remeasure = () => this.measurePreviewFit();
      const ro = new ResizeObserver(remeasure);
      ro.observe(preview);
      // Карточка меняет размер при смене appearance, даже если
      // контейнер остаётся нулевым (placement → фрейм вне потока).
      const card = preview.querySelector('.tg-card');
      if (card) ro.observe(card);
      ro.observe(actions);
      window.addEventListener('resize', remeasure);
      remeasure();

      onCleanup(() => {
        ro.disconnect();
        window.removeEventListener('resize', remeasure);
      });
    });

    // Инструменты приезжают асинхронно и меняются, когда пользователь
    // создаёт новый: поля агента должны следовать за этим списком.
    effect(() => {
      this.webhookTools();
      this.systemTools();
      // Набор подключённых тулов меняет доступность их настроек: у
      // отключённого инструмента править описание нечему.
      this.agent()?.tools;
      untracked(() => this.syncToolFields());
    });

    // Языки агента и их TTS-записи меняются прямо на странице — поля
    // голоса и настроек синтеза следуют за ними.
    effect(() => {
      this.agent();
      this.isRealtimeMode();
      untracked(() => this.syncVoiceFields());
    });

    // Effect 1: applyDeepLink после того как agent() перестанет быть null
    // (агент подгрузился) — секции реально в DOM и getElementById их найдёт.
    // ngAfterViewInit не подходит: он срабатывает СРАЗУ после mount'а
    // компонента, когда `@if (agent())` ещё скрывает шаблон.
    effect(() => {
      const a = this.agent();
      if (!a || this.deepLinkApplied) return;
      const fragment = this.initialFragment;
      // deep-link tests/talk — переключаем режим (setActiveTab само дёрнет
      // эффект-writer'а с корректным значением). До установки флага —
      // иначе effect2 всё равно первый пропишет initial 'sec-agent'.
      if (fragment === 'tests' || fragment === 'talk') {
        this.setActiveTab(fragment);
      } else if (fragment && this.settingsSections.some((s) => s.id === fragment)) {
        this.currentSection.set(fragment);
      }
      this.deepLinkApplied = true;
      // В любом случае поднимаем scrollspy после того как секции появились
      // в DOM. Deep-link (tests/talk/sec-*) — доп. поверх этого.
      queueMicrotask(() => {
        if (fragment && this.settingsSections.some((s) => s.id === fragment)) {
          const el = document.getElementById(fragment);
          if (el) el.scrollIntoView({ block: 'start' });
        }
        this.setupScrollSpy();
      });
    });

    // Effect 2: любое изменение activeTabIndex или currentSection пишется
    // в URL fragment (replaceUrl, чтобы клики по табам / скролл не
    // засоряли history). `/agents/:id#sec-widget` восстанавливается при
    // reload / share, а scroll внутри settings обновляет URL live.
    // Guard deepLinkApplied: пока не применили initial fragment из URL,
    // write-side ничего не делает — иначе initial 'sec-agent' перекроет
    // deep-link ссылку типа '#sec-widget' в первый же tick.
    effect(() => {
      const mode = this.activeTabIndex();
      const fragment = mode === 'settings' ? this.currentSection() : mode;
      if (!this.deepLinkApplied) return;
      this.syncUrlFragment(fragment);
    });
  }

  /**
   * Программное переключение режима (nav вручную вызывает при клике на
   * Tests / Talk to agent / Settings). Для settings-режима + якорной
   * навигации есть scrollToSection().
   */
  setActiveTab(mode: 'settings' | 'tests' | 'talk'): void {
    this.activeTabIndex.set(mode);
    this.cdr.markForCheck();
    // При возврате в settings — заново подключаем observer'ы к
    // возрождённым секциям.
    if (mode === 'settings') queueMicrotask(() => this.setupScrollSpy());
  }

  // Widget tab methods

  onPublishToggleChange(): void {
    this.cdr.markForCheck();
  }

  onPublishOptionsChange(): void {
    this.bumpAgent();
    this.cdr.markForCheck();
  }

  /** Триггерит identity-change корневого agent-signal — computed'ы
   *  (publicAgentPreview) видят spread как «новую версию» и пересчитываются.
   *  Вызывается из mutation handler'ов после in-place правок вложенных полей. */
  private bumpAgent(): void {
    const a = this.agent();
    if (a) this.agent.set({ ...a });
  }

  getEmbedCode(): string {
    if (!this.agent()?.id) return '';
    return `<speaknode-agent agent-id="${this.agent()!.id}"></speaknode-agent> <script src="${this.getWidgetLoaderUrl()}"></script>`;
  }

  copyEmbedCode(): void {
    const embedCode = this.getEmbedCode();
    if (embedCode) {
      navigator.clipboard.writeText(embedCode).then(() => {
        this.notificationService.showSuccess($localize`:@@agentEdit.notify.embedCopied:Embed code copied to clipboard`);
      }).catch(err => {
        console.error('Failed to copy embed code:', err);
        this.notificationService.showError($localize`:@@agentEdit.notify.embedCopyFail:Failed to copy embed code`);
      });
    }
  }

  /**
   * Loader виджета для embed-сниппета.
   *
   * ВНИМАНИЕ на домен. Виджет раздаётся ТЕМ ЖЕ сервисом, что и админка:
   * один образ, один контейнер, два порта — 8080 отдаёт SPA, 8081 отдаёт
   * widget-бандл (см. docker/nginx.conf). В Railway на них повешены два
   * домена, `app.speaknode.com` и `widget.speaknode.com`, и деплоятся они
   * ВМЕСТЕ — рассинхрона версий тут быть не может.
   *
   * А вот `app-widget.speaknode.com`, который был вбит сюда раньше, — это
   * огрызок старого отдельного сервиса. Он остался жив и отдаёт сборку от
   * 04.06.2026, собранную ещё с `darkModeSelector: false`. Админка грузила
   * именно его, второй PrimeNG затирал общие style-элементы, и тёмная тема
   * схлопывалась в светлую — это и есть SKN-425.
   */
  getWidgetLoaderUrl(): string {
    return `${environment.widgetUrl}/speaknode-widget-loader.js`;
  }

  getPublicAgentUrl(): string {
    if (!this.agent()?.id) return '';
    // Use window.location.origin to get the current domain
    return `${window.location.origin}/published/agents/${this.agent()!.id}`;
  }

  copyPublicAgentUrl(): void {
    const url = this.getPublicAgentUrl();
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        this.notificationService.showSuccess($localize`:@@agentEdit.notify.publicUrlCopied:Public agent URL copied to clipboard`);
      }).catch(err => {
        console.error('Failed to copy public agent URL:', err);
        this.notificationService.showError($localize`:@@agentEdit.notify.publicUrlCopyFail:Failed to copy public agent URL`);
      });
    }
  }

  /**
   * Пересчитывает, влезает ли превью справа от панели действий.
   *
   * Считаем не по ширине окна: контентная область ужимается и сайдбаром
   * (сворачивается), и панелью агента (тянется мышью), и ни то ни другое
   * из медиа-запроса не видно. Поэтому берём реальные прямоугольники —
   * правый край панели кнопок и ту позицию, которую превью ЗАНЯЛО БЫ у
   * правого края.
   *
   * Ширину превью меряем независимо от текущего класса: мы двигаем
   * только `bottom`, ширина от этого не меняется, поэтому обратной связи
   * «класс → размер → класс» не возникает и мигания нет.
   */
  private measurePreviewFit(): void {
    const preview = this.previewEl()?.nativeElement;
    const actions = this.actionsEl()?.nativeElement;
    if (!preview || !actions) return;

    // Меряем КАРТОЧКУ, а не контейнер: при заданном placement виджет
    // кладёт себя в `.widget-frame` с `position: fixed`, тот уходит из
    // потока, и контейнер схлопывается в нули. У карточки размер есть
    // всегда.
    const card = preview.querySelector('.tg-card') as HTMLElement | null;
    const previewRect = (card ?? preview).getBoundingClientRect();
    if (!previewRect.width) return;
    this.previewHeight.set(Math.round(previewRect.height));

    const actionsRight = actions.getBoundingClientRect().right;
    const assistant = parseFloat(
      getComputedStyle(document.documentElement).getPropertyValue('--sn-assistant-width'),
    ) || 0;

    // Где оказался бы левый край превью, стоя у правого края контента.
    const wouldBeLeft = window.innerWidth - assistant - PREVIEW_EDGE_GAP - previewRect.width;

    this.previewFitsAside.set(wouldBeLeft >= actionsRight + PREVIEW_EDGE_GAP);
  }

  // Agent menu items for top panel
  agentMenuItems = [
    {
      label: $localize`:@@agentEdit.menu.sdkDocs:SDK docs`,
      icon: 'pi pi-book',
      command: () => this.openSdkDocs()
    },
    {
      label: $localize`:@@agentEdit.menu.conversationHistory:Conversation history`,
      icon: 'pi pi-history',
      command: () => this.openConversationHistory()
    },
    {
      separator: true
    },
    {
      label: $localize`:@@agentEdit.menu.copyId:Copy Agent ID`,
      icon: 'pi pi-copy',
      command: () => this.copyAgentId()
    },
    {
      label: $localize`:@@agentEdit.menu.duplicate:Duplicate agent`,
      icon: 'pi pi-clone',
      command: () => this.duplicateAgent()
    },
    {
      separator: true
    },
    {
      label: $localize`:@@agentEdit.menu.delete:Delete agent`,
      icon: 'pi pi-trash',
      styleClass: 'text-red-500',
      command: () => this.deleteAgent()
    }
  ];

  // Custom tools menu items (dynamically generated from API)
  customToolsMenuItems: any[] = [];
  
  // Update custom tools menu when data changes
  updateCustomToolsMenu(): void {
    this.customToolsMenuItems = [];
    
    // Add available webhook tools that are not yet connected
    const availableWebhookTools = this.availableTools().filter(tool => tool.type === 'Webhook');
    availableWebhookTools.forEach(tool => {
      this.customToolsMenuItems.push({
        label: tool.title || $localize`:@@agentEdit.tools.unnamed:Unnamed Tool`,
        icon: 'pi pi-plus',
        command: () => this.addToolToAgent(tool.id || '')
      });
    });

    // Add separator if we have webhook tools
    if (availableWebhookTools.length > 0) {
      this.customToolsMenuItems.push({
        separator: true
      });
    }


    // Always show static menu options at the end
    this.customToolsMenuItems.push({
      label: $localize`:@@agentEdit.tools.addWebhook:Add webhook tool`,
      icon: 'pi pi-link',
      command: () => this.createNewWebhookTool()
    });
    
    this.cdr.markForCheck();
  }

  // Custom tools examples removed - now using real data from API

  // Default Tools toggle states (disabled by default)
  defaultTools = {
    endCall: { enabled: false, label: 'End call', icon: 'pi pi-phone' },
    transferToAgent: { enabled: false, label: 'Transfer to Agent', icon: 'pi pi-users' }
  };

  // // Webhook tools data
  // webhookTools: HttpTool[] = [];
  // selectedWebhookTool: CreateToolCommand = this.createEmptyTool();
  // 
  // // Sidebar state for webhook tool creation
  // showWebhookSidebar: boolean = false;
  // isEditingWebhookTool: boolean = false;


  // ViewChild references for textareas
  @ViewChild('agentTabList') agentTabListRef?: TabList;
  @ViewChild('firstMessageTextarea') firstMessageTextarea!: ElementRef<HTMLTextAreaElement>;
  @ViewChild('systemPromptTextarea') systemPromptTextarea!: ElementRef<HTMLTextAreaElement>;



  ngOnInit(): void {
    // Clear all variable sources from previous instances
    this.dynamicVariablesService.clearAllSources();

    // Поля страницы агент собирает сам — они объявлены в шаблоне
    // поверхностью snAssistantSurface. Здесь регистрируем только действия:
    // им нужен осмысленный результат, а не слепой клик по кнопке.
    this.unregisterAssistantActions = this.assistantUi.registerActions(this.assistantActions);
    // Выбор модели живёт не в форме, а в своём диалоге — такие поля
    // страница объявляет сама. Варианты подставляем на лету: иерархия
    // приезжает с бэка уже после первого рендера.
    this.unregisterAssistant = this.assistantUi.registerFields(this.assistantModelFields);

    const agentId = this.route.snapshot.paramMap.get('id');

    // Подписываемся на SignalR AgentUpdated — когда ассистент через MCP меняет
    // нашего агента, бэк рассылает событие → подменяем модель в UI без рефреша.
    this.signalr.agentUpdated$
      .pipe(
        filter(a => !!agentId && a.id === agentId),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(updated => {
        this.agent.set(updated);
        this.sortLanguageLocales();
        this.publishAssistantContext();
        this.cdr.markForCheck();
      });

    // ToolCreated/Updated — обновляем allTools, чтобы новые тулы появились
    // в списках Available/Webhook/System без перезагрузки страницы.
    merge(this.signalr.toolCreated$, this.signalr.toolUpdated$)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.toolsService.apiToolsGet().subscribe(tools => {
          this.allTools.set(tools);
          this.cdr.markForCheck();
        });
      });
    if (agentId) {
      // Parallel load auxiliary data, then load agent.
      forkJoin({
        spaces: this.spacesService.apiSpacesGet(),
        modelsHierarchy: this.modelsService.apiModelsHierarchyGet(),
        tools: this.toolsService.apiToolsGet(),
        agents: this.agentsService.apiAgentsGet(),
        languages: this.languagesService.apiLanguagesLanguageLocalesGet()
      }).pipe(
        switchMap((data) => {
          // Set auxiliary data
          this.spaces = data.spaces;
          this.modelsHierarchy = data.modelsHierarchy;
          this.allTools.set(data.tools);
          this.availableAgents = data.agents.agents || [];
          this.languagesData = data.languages;
          this.agentsLoaded = true;
          this.buildLanguageOptions();
          this.buildLlmTreeNodes();
          this.buildTtsTreeNodes();
          this.buildSttTreeNodes();
          this.cdr.markForCheck();

          // Now load agent
          return this.agentsService.apiAgentsIdGet({ id: agentId });
        })
      ).subscribe({
        next: (agent) => {
          this.agent.set(agent);
          this.sortLanguageLocales();
          this.initializeAgentData();
          // Extract variables from all text sources and sync with schema
          this.updateVariablesFromAllSources();
          this.publishAssistantContext();
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading data:', error);
          this.notificationService.showError($localize`:@@agentEdit.notify.loadFail:Failed to load data`);
          this.cdr.markForCheck();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    // Наиболее универсальный вход в scrollspy: если агент был отдан
    // синхронно (например при переходе из соседней страницы, где
    // AgentEditComponent уже был закэширован Angular Router'ом) — секции
    // уже в DOM. Effect1 в constructor'е тоже дёргает setupScrollSpy,
    // но там путь через apply-deep-link. Тут страхуемся для случая без
    // fragment'а.
    if (this.agent()) this.setupScrollSpy();
  }

  ngOnDestroy(): void {
    // Clear all variable sources when component is destroyed
    this.dynamicVariablesService.clearAllSources();
    this.assistantContext.clear();
    this.unregisterAssistant?.();
    this.unregisterToolFields?.();
    this.unregisterAssistantActions?.();
    // Поля голоса регистрируются отдельно (syncVoiceFields — по набору на
    // язык) и снимались только при пересборке набора. Без этой строки они
    // оставались в реестре после уходa со страницы: агент приходил на
    // /tests и получал в ответ поля редактора агента — поверхность,
    // которой на экране уже нет (SKN-473).
    this.unregisterVoiceFields?.();
    this.teardownScrollSpy();
  }

  // ======================================================================
  // Встроенный агент: поля и действия этой страницы
  // ======================================================================
  // Агент не ходит в бэк за изменениями — он работает ровно теми же
  // полями и кнопками, что и пользователь. Здесь описано, что именно на
  // странице агента ему доступно; подсветку и подтверждение берёт на себя
  // AssistantUiService, а якоря в шаблоне — директива snAssistantAnchor.

  private unregisterAssistant?: () => void;
  private unregisterAssistantActions?: () => void;

  /** Ссылка на вкладку тестов — нужна, чтобы запустить прогон. */
  private readonly testsTab = viewChild(AgentTestsTabComponent);
  private readonly modelSelectors = viewChildren(ModelSelectorButton);

  /**
   * Показать поле пользователю перед предложением. Настройки живут на
   * вкладке settings — если агент правит их, пока открыта вкладка тестов,
   * пользователь увидит не подсветку, а пустоту.
   *
   * Стрелка, а не метод: уезжает во входной параметр директивы поверхности,
   * и `this` должен остаться компонентом.
   */
  readonly revealAgentField = (): void => {
    if (this.activeTabIndex() !== 'settings') this.setActiveTab('settings');
    // Незаполненный селектор моделей раскрыт таблицей во весь экран:
    // сворачиваем, иначе подсветка обведёт таблицу, а карточка
    // предложения окажется под ней, за краем экрана.
    for (const selector of this.modelSelectors()) selector.collapse();
  };

  /**
   * Варианты моделей для агента: версии из уже загруженной иерархии,
   * подписанные по-человечески. Пользователь выбирает их в диалоге, агент —
   * из этого списка; в обоих случаях запись идёт одним и тем же путём.
   *
   * Список ограничен тем, что вернул бэк с текущим «только рекомендованные»:
   * если ничего не подходит, агент просит MCP отдать полный перечень.
   */
  private versionOptionsFor(code: string): AssistantFieldOption[] {
    const type = this.modelsHierarchy?.model_types?.find(t => t.code === code);
    const out: AssistantFieldOption[] = [];
    for (const gateway of type?.gateways ?? []) {
      for (const model of gateway.models ?? []) {
        for (const version of model.versions ?? []) {
          if (!version.id || version.is_active === false) continue;
          const label = [gateway.code, model.code, version.version].filter(Boolean).join(' / ');
          out.push({ value: version.id, label: version.is_recommended ? `${label} ★` : label });
        }
      }
    }
    return out;
  }

  private modelVersionOptions(): AssistantFieldOption[] {
    return this.versionOptionsFor(this.isRealtimeMode() ? 'realtime' : 'llm');
  }

  /** Параметры сэмплирования пишутся в тот же слот, что и выбор из тестера. */
  private patchActivePrimary(patch: Partial<AgentModelVersionEntryDto>): void {
    if (!this.agent()) return;
    const chain = this.cloneActiveChain();
    const slot = this.activeRowSlot(chain, 0);
    if (!slot) return;
    Object.assign(slot, patch);
    this.replaceChain(this.isRealtimeMode() ? 'realtime' : 'llm', chain);
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  /**
   * Голоса по версии TTS-модели. Список приезжает с бэка на каждую
   * версию отдельно; держим его здесь, чтобы у поля голоса были
   * человеческие варианты, а не «введите GUID».
   */
  private readonly voicesByVersion = signal<Record<string, Array<{ id: string; name: string }>>>({});

  private loadVoicesFor(versionId: string | null | undefined): void {
    if (!versionId || this.voicesByVersion()[versionId]) return;
    this.modelsService
      .apiModelsVersionsModelVersionIdVoicesGet({ modelVersionId: versionId })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: result => {
          const voices = (result.voices ?? [])
            .filter(v => v.is_active !== false)
            .map(v => ({
              id: v.id ?? '',
              name: [v.name, v.description].filter(Boolean).join(' · ') || (v.code ?? v.id ?? ''),
            }))
            .filter(v => v.id);
          this.voicesByVersion.update(map => ({ ...map, [versionId]: voices }));
        },
        error: () => this.voicesByVersion.update(map => ({ ...map, [versionId]: [] })),
      });
  }

  /**
   * Поля голоса и настроек синтеза — по одному набору на язык агента.
   *
   * В интерфейсе они живут в ящике выбора модели: человек открывает
   * тестер, слушает голос и жмёт «выбрать». Для агента ящик — не место
   * действия, а способ показать: состояние всё равно принадлежит
   * странице, и пишется оно теми же методами, что дёргает ящик.
   */
  private unregisterVoiceFields?: () => void;

  private syncVoiceFields(): void {
    this.unregisterVoiceFields?.();
    const locales = this.agent()?.language_locales ?? [];
    const specs: AssistantFieldSpec[] = [];

    for (const locale of locales) {
      const code = this.getLanguageCodeForLocale(locale) || locale.locale_id || locale.id || '';
      if (!code || !locale.id) continue;
      const language = this.getLanguageDisplayName(code) || code;
      this.loadVoicesFor(this.getTtsVersionIdForLocale(locale));

      specs.push({
        id: `agent.ttsModelVersion.${code}`,
        surface: 'agent',
        localId: `ttsModelVersion.${code}`,
        label: $localize`:@@agentEdit.tts.label:TTS Model` + ` — ${language}`,
        kind: 'enum',
        optionsOf: () => this.versionOptionsFor('tts'),
        allowUnlisted: true,
        read: () => this.getTtsVersionIdForLocale(locale) ?? '',
        write: value => {
          const id = String(value ?? '');
          this.onTtsModelSelected(locale, id);
          this.loadVoicesFor(id);
        },
        reveal: () => this.revealVoiceSection(),
        available: () => !!this.agent() && !this.isRealtimeMode(),
      });

      specs.push({
        id: `agent.voice.${code}`,
        surface: 'agent',
        localId: `voice.${code}`,
        label: $localize`:@@agentEdit.realtime.voice.label:Голос` + ` — ${language}`,
        kind: 'enum',
        optionsOf: () => {
          const versionId = this.isRealtimeMode()
            ? this.activePrimaryVersionId()
            : this.getTtsVersionIdForLocale(locale);
          this.loadVoicesFor(versionId);
          return (this.voicesByVersion()[versionId ?? ''] ?? []).map(v => ({
            value: v.id,
            label: v.name,
          }));
        },
        read: () =>
          (this.isRealtimeMode() ? this.activePrimaryVoiceId() : this.getVoiceIdForLocale(locale)) ?? '',
        write: value => this.setVoiceForLocale(locale, String(value ?? '')),
        reveal: () => this.revealVoiceSection(),
        available: () => !!this.agent(),
      });

      specs.push({
        id: `agent.ttsProperties.${code}`,
        surface: 'agent',
        localId: `ttsProperties.${code}`,
        label: $localize`:@@agentEdit.tts.properties:Synthesis settings` + ` — ${language}`,
        kind: 'json',
        read: () => this.getTtsValues(locale),
        write: value => {
          const parsed = typeof value === 'string' ? JSON.parse(value) : value;
          this.onTtsPropertiesChange(locale, (parsed ?? {}) as { [key: string]: any });
        },
        reveal: () => this.revealVoiceSection(),
        available: () => !!this.agent() && !this.isRealtimeMode() && this.hasTtsProperties(locale),
      });
    }

    this.unregisterVoiceFields = this.assistantUi.registerFields(specs);
  }

  /** Голос пишем тем же путём, что тестер: через upsert TTS-записи локали. */
  private setVoiceForLocale(locale: AgentLanguageLocaleDto, voiceId: string): void {
    if (this.isRealtimeMode()) {
      const chain = this.cloneActiveChain();
      if (!chain.length) return;
      chain[0].voice_id = voiceId || null;
      this.replaceChain('realtime', chain);
      this.bumpAgentSignal();
      return;
    }
    const entry = this.ttsEntryForLocale(locale.id);
    if (!entry?.model_version_id || !locale.id) return;
    this.upsertTtsForLocale(locale.id, {
      model_version_id: entry.model_version_id,
      voice_id: voiceId || null,
    });
    this.bumpAgentSignal();
  }

  /** Показать раздел голосов: правка не должна происходить за кадром. */
  private revealVoiceSection(): void {
    this.revealAgentField();
    this.currentSection.set('sec-voice');
    queueMicrotask(() => document.getElementById('sec-voice')?.scrollIntoView({ block: 'start' }));
  }

  private readonly assistantModelFields: AssistantFieldSpec[] = [
    {
      id: 'agent.modelVersion',
      surface: 'agent',
      localId: 'modelVersion',
      label: $localize`:@@agentEdit.llm.select:Select LLM Model`,
      kind: 'enum',
      optionsOf: () => this.modelVersionOptions(),
      allowUnlisted: true,
      read: () => this.activePrimaryVersionId(),
      write: value => this.onActiveRowModelSelected(0, String(value ?? '')),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent(),
    },
    {
      id: 'agent.sttModelVersion',
      surface: 'agent',
      localId: 'sttModelVersion',
      label: $localize`:@@agentEdit.stt.select:Select STT Model`,
      kind: 'enum',
      optionsOf: () => this.versionOptionsFor('stt'),
      allowUnlisted: true,
      read: () => this.sttEntry()?.model_version_id ?? '',
      write: value => this.onSttModelSelected(String(value ?? '')),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent() && !this.isRealtimeMode(),
    },
    {
      id: 'agent.temperature',
      surface: 'agent',
      localId: 'temperature',
      label: $localize`:@@modelTester.llm.temperature:Temperature`,
      kind: 'number',
      read: () => this.activePrimaryTemperature(),
      write: value => this.patchActivePrimary({ temperature: value as number }),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent() && this.isTemperatureAllowed,
    },
    {
      id: 'agent.topP',
      surface: 'agent',
      localId: 'topP',
      label: 'top-P',
      kind: 'number',
      read: () => this.activePrimaryTopP(),
      write: value => this.patchActivePrimary({ top_p: value as number }),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent(),
    },
    {
      id: 'agent.topK',
      surface: 'agent',
      localId: 'topK',
      label: 'top-K',
      kind: 'number',
      read: () => this.activePrimaryTopK(),
      write: value => this.patchActivePrimary({ top_k: value as number }),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent(),
    },
    {
      id: 'agent.maxOutputTokens',
      surface: 'agent',
      localId: 'maxOutputTokens',
      label: $localize`:@@modelTester.llm.maxOutputTokens:Max output tokens`,
      kind: 'number',
      read: () => this.activePrimaryMaxOutputTokens(),
      write: value => this.patchActivePrimary({ max_output_tokens: value as number }),
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent(),
    },
    {
      // Провайдерские настройки модели — те, что в ящике лежат под
      // «Additional properties». Правим целиком: они осмысленны вместе,
      // и схема у каждой модели своя.
      id: 'agent.additionalProperties',
      surface: 'agent',
      localId: 'additionalProperties',
      label: $localize`:@@agentEdit.llm.additionalProperties:Additional properties`,
      kind: 'json',
      read: () => valueEntriesToDict(this.activePrimary()?.additional_properties),
      write: value => {
        const parsed = typeof value === 'string' ? JSON.parse(value) : value;
        this.patchActivePrimary({
          additional_properties: dictToValueEntries((parsed ?? {}) as { [key: string]: any }),
        });
      },
      reveal: () => this.revealAgentField(),
      available: () => !!this.agent(),
    },
    {
      // Набор прикреплённых тестов — список, а не кнопка: «прикрепить» и
      // «открепить» на бэке это одна запись всего набора целиком.
      id: 'agent.attachedTests',
      surface: 'agent',
      localId: 'attachedTests',
      label: $localize`:@@agentEdit.tab.tests:Tests`,
      kind: 'text',
      read: () => this.testsTab()?.attachedTestNames().join(', ') ?? '',
      write: value => this.setAttachedTests(String(value ?? '')),
      validate: () => this.attachedTestsError,
      reveal: () => this.setActiveTab('tests'),
      available: () => this.activeTabIndex() === 'tests' && !!this.testsTab(),
    },
  ];

  private attachedTestsError: string | null = null;

  /** Приводит набор прикреплённых тестов к списку имён, как его видит человек. */
  private setAttachedTests(names: string): void {
    const tab = this.testsTab();
    if (!tab) {
      this.attachedTestsError = 'Open the Tests tab first — run the agent.openTests action.';
      return;
    }
    const wanted = names.split(',').map(name => name.trim()).filter(Boolean);
    const unknown = tab.unknownTestNames(wanted);
    if (unknown.length) {
      this.attachedTestsError =
        `No such test: ${unknown.join(', ')}. Existing tests: ${tab.allTestNames().join(', ')}.`;
      return;
    }
    this.attachedTestsError = null;
    tab.setAttachedByNames(wanted);
  }

  /**
   * Подключение webhook-инструментов к агенту.
   *
   * В интерфейсе это выпадающее меню «Add tool» и корзина в списке — то
   * есть действия, а не поля. Для агента удобнее и честнее булево поле на
   * каждый инструмент: «подключён или нет». Читается так же, как человек
   * видит список, пишется теми же методами, что и меню с корзиной.
   *
   * Список приезжает с бэка, поэтому поля переобъявляются при каждом его
   * изменении — отсюда effect, а не статический массив.
   */
  private unregisterToolFields?: () => void;

  /**
   * Поля инструментов: подключение плюс НАСТРОЙКИ каждого подключённого.
   *
   * Переключателя было мало. Настройки инструмента — своё описание для LLM
   * и запрет прерываний — живут в дравере, а дравер закрыт, пока его не
   * откроют мышью. Компонента нет в дереве → его поля не зарегистрированы →
   * агент их не видит и честно отвечает «этого нельзя изменить», хотя в
   * интерфейсе шестерёнка рядом с инструментом открывает ровно эти поля.
   *
   * Поэтому регистрирует их страница, а не дравер: она владеет
   * `agent().tools` и знает про инструмент всё, не открывая ничего. Дравер
   * остаётся способом посмотреть глазами — его открывает `reveal`, когда
   * агент предлагает правку.
   */
  private syncToolFields(): void {
    this.unregisterToolFields?.();

    const specs: AssistantFieldSpec[] = [];

    for (const tool of this.webhookTools()) {
      const id = tool.id ?? '';
      if (!id) continue;
      specs.push({
        id: `agent.webhookTool.${id}`,
        surface: 'agent',
        localId: `webhookTool.${id}`,
        label: tool.title ?? id,
        description: tool.description ?? undefined,
        kind: 'boolean' as const,
        read: () => (this.agent()?.tools ?? []).some(t => t.tool_id === id),
        write: value => {
          if (value === true) this.addToolToAgent(id);
          else this.removeToolFromAgent(id);
        },
        reveal: () => this.revealAgentField(),
        available: () => !!this.agent() && !!id,
      });
      specs.push(...this.toolSettingsSpecs(tool, tool.title ?? id, () => this.editTool(id)));
    }

    for (const entry of this.systemTools()) {
      const tool = entry.tool;
      if (!tool.id) continue;
      specs.push(...this.toolSettingsSpecs(tool, entry.title, () => this.editSystemTool(tool)));
    }

    this.unregisterToolFields = this.assistantUi.registerFields(specs);
  }

  /**
   * Пара полей, одинаковая у любого инструмента: описание для LLM и запрет
   * прерываний. Оба лежат на СВЯЗКЕ агент↔инструмент (`AgentToolDto`), а не
   * на самом инструменте: один и тот же инструмент у разных агентов
   * описывается по-разному.
   *
   * `available` завязан на подключённость: у неподключённого инструмента
   * связки нет, и писать некуда. Агент увидит поле недоступным и сначала
   * включит инструмент — это отдельное, уже существующее поле.
   */
  private toolSettingsSpecs(
    tool: ToolSummary,
    title: string,
    openDrawer: () => void,
  ): AssistantFieldSpec[] {
    const id = tool.id ?? '';
    const code = tool.code ?? id;
    const connected = () => this.getAgentTool(id);
    const reveal = () => {
      this.revealAgentField();
      openDrawer();
    };

    /**
     * Настройки живут на СВЯЗКЕ агент↔инструмент, и у неподключённого
     * инструмента связки нет. Раньше поля в этом случае объявлялись
     * недоступными — и пропадали из getPageFields целиком, потому что
     * listFields() отсеивает недоступные. Агент честно отвечал «интерфейс не
     * отдаёт поля инструментов», хотя настройка существует: её просто негде
     * держать, пока инструмент выключен.
     *
     * Поэтому подключаем сам: человек в этом месте делает ровно то же —
     * щёлкает переключатель и открывает шестерёнку. Правка видна в карточке
     * предложения вместе с включением, так что молча ничего не происходит.
     */
    const link = (): AgentToolDto | undefined => {
      if (!connected()) this.addToolToAgent(id);
      return connected();
    };

    return [
      {
        id: `systemTool.${code}.toolDescription`,
        surface: 'systemTool',
        localId: 'toolDescription',
        label: title,
        kind: 'text' as const,
        // Пустое описание — законное значение: инструмент берёт дефолтное,
        // так и написано в плейсхолдере поля. Поэтому null, а не «».
        read: () => connected()?.custom_description ?? null,
        write: value => {
          const agentTool = link();
          if (!agentTool) return;
          const text = typeof value === 'string' ? value.trim() : '';
          agentTool.custom_description = text.length > 0 ? text : null;
          this.markToolsDirty();
        },
        reveal,
        available: () => !!this.agent() && !!id,
      },
      {
        id: `systemTool.${code}.disableInterruptions`,
        surface: 'systemTool',
        localId: 'disableInterruptions',
        label: title,
        kind: 'boolean' as const,
        read: () => connected()?.custom_disable_interruptions ?? false,
        write: value => {
          const agentTool = link();
          if (!agentTool) return;
          agentTool.custom_disable_interruptions = value === true;
          this.markToolsDirty();
        },
        reveal,
        available: () => !!this.agent() && !!id,
      },
      ...(code === 'transfer-to-operator'
        ? [{
            /**
             * Наш номер для звонка оператору вне телефонного разговора.
             * Лежит на АГЕНТЕ, а не на связке с инструментом: телеком-аккаунт
             * один на агента. Показывается в дравере этого инструмента,
             * потому что смысл у поля ровно один — этот перевод.
             */
            id: `systemTool.${code}.defaultPhoneNumber`,
            surface: 'systemTool',
            localId: 'defaultPhoneNumber',
            label: title,
            kind: 'text' as const,
            read: () => this.agent()?.default_phone_number_agent_id ?? null,
            write: (value: unknown) => {
              const id = typeof value === 'string' && value.trim() ? value.trim() : null;
              this.onDefaultPhoneNumberChange(id);
            },
            reveal,
            available: () => !!this.agent() && !!id,
          }]
        : []),
      ...(RULE_TOOL_CODES.includes(code)
        ? [{
            /**
             * Правила инструмента — ОДНИМ полем, а не набором контролов.
             *
             * В разметке каждое правило это своя строка с condition, номером,
             * режимом; адресовать их по отдельности нечем — id поля строится
             * из поверхности и имени контрола, и у трёх правил получилось бы
             * три одинаковых `systemTool.condition`. Поштучно тут не выйдет.
             *
             * Поэтому агент заполняет весь массив целиком, а рендер — дело
             * сигнала: подменили tools, список перерисовался. Тот же приём,
             * которым уже отдаются схема переменных агента и значения
             * переменных строки обзвона.
             */
            id: `systemTool.${code}.transferRules`,
            surface: 'systemTool',
            localId: 'transferRules',
            label: title,
            kind: 'json' as const,
            read: () => connected()?.transfer_rules ?? [],
            write: (value: unknown) => {
              const agentTool = link();
              if (!agentTool) return;
              const parsed = typeof value === 'string' ? JSON.parse(value) : value;
              const rules = Array.isArray(parsed) ? (parsed as TransferRuleDto[]) : [];
              // Индекс — то, чем инструмент адресуется из LLM во время
              // разговора (transfer_rule_index), и он обязан совпадать с
              // порядком в массиве. Проставляем сами: модель о нём знать не
              // обязана, а разъехавшийся индекс уводит звонок не туда.
              agentTool.transfer_rules = rules.map((rule, index) => ({
                ...rule,
                id: rule.id ?? crypto.randomUUID(),
                transfer_rule_index: index,
              }));
              this.markToolsDirty();
            },
            reveal,
            available: () => !!this.agent() && !!id,
            validate: () => this.validateTransferRules(code, connected()?.transfer_rules ?? []),
          }]
        : []),
    ];
  }

  /**
   * Что обязано быть в правиле — зависит от инструмента, ровно как в дравере.
   * Возвращаем текст, а не булево: он уезжает модели результатом тул-колла,
   * и по нему она чинит правило сама, вместо того чтобы оставить форму
   * невалидной и молча пойти дальше.
   */
  private validateTransferRules(code: string, rules: TransferRuleDto[]): string | null {
    const problems: string[] = [];

    rules.forEach((rule, index) => {
      const missing: string[] = [];
      if (!rule.condition?.trim()) missing.push('condition');

      if (code === 'transfer-to-operator') {
        if (!rule.transfer_to_phone_number?.trim()) missing.push('transfer_to_phone_number');
        if (!rule.transfer_mode) missing.push('transfer_mode');
      } else if (code === 'transfer-to-agent') {
        if (!rule.transfer_to_agent_id) missing.push('transfer_to_agent_id');
      } else if (code === 'get-knowledge') {
        if (!rule.answer?.trim()) missing.push('answer');
      }

      if (missing.length > 0) {
        problems.push(`rule #${index}: ${missing.join(', ')}`);
      }
    });

    return problems.length > 0
      ? `Incomplete transfer rules — ${problems.join('; ')}`
      : null;
  }

  /**
   * Правка внутри связки — мутация объекта ВНУТРИ сигнала, и сама по себе
   * она computed'ы не будит (тот же случай, что ломал список webhook-тулов).
   * Подменяем массив, чтобы список и превью перерисовались.
   */
  private markToolsDirty(): void {
    const current = this.agent();
    if (!current?.tools) return;
    this.agent.set({ ...current, tools: [...current.tools] });
  }

  private readonly assistantActions: AssistantActionSpec[] = [
    {
      id: 'agent.save',
      label: $localize`:@@common.save:Save`,
      description: 'Persist every pending change of this agent. Field edits do nothing until this runs.',
      run: () => this.saveAgentAsync(),
      available: () => !!this.agent() && !this.isSaving,
    },
    {
      /**
       * Кнопка «Сохранить» в дравере инструмента.
       *
       * Она НЕ пишет на бэк: валидирует правила и переносит правку в модель
       * агента, после чего дравер закрывается. На сервер всё уезжает общим
       * agent.save. Агенту это нужно ровно за валидацию — тем же путём, каким
       * идёт человек: закрыл инструмент, увидел ошибку, если правило неполное.
       */
      id: 'agent.tool.apply',
      label: $localize`:@@common.save:Save`,
      description:
        'Close the open tool drawer, applying its edits to the agent. Validates the tool first and ' +
        'refuses on an incomplete transfer rule. Saves nothing to the server — run agent.save for that.',
      run: () => {
        const cmp = this.systemToolCmp() ?? this.webhookToolCmp();
        if (!cmp) return 'No tool drawer is open';
        if ('save' in cmp) cmp.save();
        else cmp.onSave();
        return 'Tool drawer applied and closed';
      },
      available: () => this.showSystemToolDrawer || this.showWebhookDrawer,
    },
    {
      /**
       * «Назад» в дравере: закрывает, ничего не применяя. Снимка правок
       * дравер не держит, так что это именно «закрыть», а не «откатить» —
       * так и написано, чтобы агент не рассчитывал на отмену.
       */
      id: 'agent.tool.close',
      label: $localize`:@@common.back:Back`,
      description:
        'Close the open tool drawer without applying its edits. Note it does not undo field changes ' +
        'already written into the agent — it only closes the drawer.',
      run: () => {
        const cmp = this.systemToolCmp() ?? this.webhookToolCmp();
        if (!cmp) return 'No tool drawer is open';
        if ('cancel' in cmp) cmp.cancel();
        else cmp.onCancel();
        return 'Tool drawer closed';
      },
      available: () => this.showSystemToolDrawer || this.showWebhookDrawer,
    },
    {
      id: 'agent.openTests',
      label: $localize`:@@agentEdit.tab.tests:Tests`,
      description: 'Open the evaluation tests tab of this agent (attached tests and past runs).',
      run: async () => {
        this.setActiveTab('tests');
        const tab = await this.waitForTestsTab();
        return { opened: true, attachedTests: tab?.attachedTestCount() ?? 0 };
      },
      available: () => !!this.agent(),
    },
    {
      id: 'agent.runEvaluationTests',
      label: $localize`:@@agentTests.runAll:Run all tests`,
      description:
        'Start an evaluation run of every test attached to this agent. Each test is a real conversation ' +
        'with the agent, so a run takes time and costs money — this is how you verify a prompt change.',
      dangerous: true,
      run: async () => {
        this.setActiveTab('tests');
        const tab = await this.waitForTestsTab();
        if (!tab) return { started: false, error: 'The tests tab did not open.' };
        if (tab.attachedTestCount() === 0) {
          return {
            started: false,
            error: 'No tests are attached to this agent yet. Attach one first, or suggest creating it.',
          };
        }
        tab.runAll();
        return {
          started: true,
          tests: tab.attachedTestCount(),
          note: 'The run is asynchronous — results show up in the Runs tab as conversations finish.',
        };
      },
      available: () => !!this.agent(),
    },
    {
      id: 'agent.delete',
      label: $localize`:@@agentEdit.menu.delete:Delete agent`,
      description:
        'Delete this agent for good. Numbers pointing at it stop answering, campaigns using it stop ' +
        'calling, and its conversations lose their author. Copy it first if there is any doubt.',
      dangerous: true,
      run: () => this.deleteAgentAsync(),
      available: () => !!this.agent()?.id,
    },
    {
      id: 'agent.duplicate',
      label: $localize`:@@agentEdit.menu.duplicate:Duplicate agent`,
      description:
        'Open the "duplicate agent" page pre-filled from this agent. Use it to try a risky change on a ' +
        'copy instead of the live agent. Unsaved edits on this page are not carried over.',
      run: async () => {
        this.duplicateAgent();
        return { navigatedTo: `/agents/${this.agent()?.id}/duplicate` };
      },
      available: () => !!this.agent()?.id,
    },
  ];

  /**
   * Удаление тем же путём, что кнопкой: открываем диалог подтверждения и
   * сразу подтверждаем — своё подтверждение агент уже получил в карточке
   * предложения, второе окно ему нечем нажать.
   */
  private deleteAgentAsync(): Record<string, unknown> {
    const agent = this.agent();
    if (!agent?.id) return { ok: false, error: 'No agent is open.' };
    this.deleteAgent();
    this.onDeleteConfirm();
    return { ok: true, deleted: agent.title ?? agent.id };
  }

  /** Promise-обёртка над saveAgent — тулу нужен результат, а не колбэк. */
  private saveAgentAsync(): Promise<Record<string, unknown>> {
    return new Promise(resolve => {
      this.saveAgent(
        () => resolve({ saved: true, agentId: this.agent()?.id }),
        message => resolve({ saved: false, error: message }),
      );
    });
  }

  /** Вкладка тестов монтируется после переключения — ждём её появления. */
  private async waitForTestsTab(timeoutMs = 3000): Promise<AgentTestsTabComponent | undefined> {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
      const tab = this.testsTab();
      if (tab) return tab;
      await new Promise<void>(resolve => setTimeout(resolve, 100));
    }
    return undefined;
  }

  private publishAssistantContext(): void {
    if (!this.agent()) return;
    // В system кладём только указатель (тип+id+ярлык) — свежие данные модель
    // подтянет через MCP-тулы (apiAgentsIdGet и т.п.) когда они ей нужны.
    // Полный JSON-дамп раздул бы system на килобайты и протух бы после save.
    this.assistantContext.set({
      description: 'Agent editor',
      value: { type: 'agent', id: this.agent()!.id, title: this.agent()!.title },
    });
  }

  private initializeAgentData(): void {
    if (!this.agent()) return;

    // Initialize default fields with new language_locales structure
    if (!this.agent()!.language_locales) {
      this.agent()!.language_locales = [];
    }

    // Initialize model_versions if null (плоский список всех моделей агента)
    if (!this.agent()!.model_versions) {
      this.agent()!.model_versions = [];
    }

    // Initialize public_agent if not present
    if (!this.agent()!.public_agent) {
      this.agent()!.public_agent = {
        languages: undefined,
        publish_options: {
          language_dropdown: false,
          appearance: 'Full',
          placement: 'BottomRight'
        }
      };
    }

    const publicAgent = this.agent()!.public_agent!;

    // Ensure publish_options exists in public_agent
    if (!publicAgent.publish_options) {
      publicAgent.publish_options = {
        language_dropdown: false,
        appearance: 'Full',
        placement: 'BottomRight'
      };
    }

    // Initialize appearance and placement if not present
    if (!publicAgent.publish_options.appearance) {
      publicAgent.publish_options.appearance = 'Full';
    }
    if (!publicAgent.publish_options.placement) {
      publicAgent.publish_options.placement = 'BottomRight';
    }
    if (publicAgent.publish_options.main_label === undefined) {
      publicAgent.publish_options.main_label = null;
    }
    // Дефолт show_transcript = true: разговор виден. Явное false — только
    // если каллер сам выключил тумблер.
    if (publicAgent.publish_options.show_transcript === undefined) {
      publicAgent.publish_options.show_transcript = true;
    }

    // Первичная инициализация preview-snapshot'а для вкладки Talk to agent.
    // Дальше synchронизация происходит через onPublishOptionsChange (ngModel).
    this.bumpAgent();

    // Ensure we have a primary language
    if (!this.primaryLanguageLocale) {
      // Find default English locale (en-US) or fallback to first English option
      const defaultEnglish = this.languageOptions.find(opt => opt.value === 'en-US') ||
                            this.languageOptions.find(opt => opt.languageCode === 'en') ||
                            this.languageOptions[0];

      if (defaultEnglish?.value) {
        const locale = this.findLocaleByFullCode(defaultEnglish.value);
        if (locale) {
          this.agent()!.language_locales!.push({
            language_locale_id: locale.id!,
            is_default: true,
            first_message: 'Hello! How can I help you today?'
          });
        }
      }
    }

    // Default temperature handling lives on llm_versions[i].temperature
    // now — UI seeds 0.7 in the tester slider when nothing is set, so no
    // backfill needed here.

    // Set selected model version
    const primaryVersionId = this.primaryLlmVersionId;
    if (primaryVersionId) {
      this.setSelectedModelFromId(primaryVersionId);
    } else {
      this.selectedLlmModelVersion = null;
    }

    // Set selected STT model version
    const sttId = this.sttEntry()?.model_version_id;
    if (sttId) {
      this.setSelectedSttModelFromId(sttId);
    } else {
      this.selectedSttModelVersion = null;
    }

    // Set selected TTS model version from primary language (user selection or default)
    const primaryLocale = this.primaryLanguageLocale;
    if (primaryLocale) {
      const effectiveTtsVersionId = this.getEffectiveTtsVersionIdForLocale(primaryLocale);
      if (effectiveTtsVersionId) {
        this.setSelectedTtsModelFromId(effectiveTtsVersionId);
        // Also update primaryTtsModel for UI display
        const findNodeByKey = (nodes: any[], key: string): any => {
          for (const node of nodes) {
            if (node.key === key) return node;
            if (node.children) {
              const found = findNodeByKey(node.children, key);
              if (found) return found;
            }
          }
          return null;
        };
        const foundNode = findNodeByKey(this.getPrimaryLanguageTtsNodes(), effectiveTtsVersionId);
        if (foundNode) {
          this.primaryTtsModel = foundNode;
        }
      } else {
        this.selectedTtsModelVersion = null;
        this.primaryTtsModel = null;
      }
    } else {
      this.selectedTtsModelVersion = null;
      this.primaryTtsModel = null;
    }

    // Set default voice language to agent's primary language (now full_code)
    this.selectedVoiceLanguage = this.languageModel.primaryLanguage || 'en-US';

    // Initialize voice configuration
    this.initializeVoiceConfiguration();

    // Update states
    this.updateTemperaturePresetsState();
    this.initializeBreadcrumb();
    this.updateCustomToolsMenu();
    this.updateAvailableLanguagesMenu();
    this.syncLanguageModel();
  }





  /**
   * @param onError вызывается при отказе бэка или проваленной валидации.
   *   Нужен встроенному агенту: он ждёт результат сохранения, и без
   *   ветки ошибки его tool-call висел бы вечно.
   */
  saveAgent(onSuccess?: () => void, onError?: (message: string) => void): void {
    if (!this.agent() || this.isSaving) {
      onError?.('Save is already in progress');
      return;
    }

    // Validate that at least one language has is_default: true
    if (!this.validateDefaultLanguage()) {
      const message = $localize`:@@agentEdit.notify.needDefaultLang:At least one language must be set as default`;
      this.notificationService.showError(message);
      onError?.(message);
      return;
    }

    this.isSaving = true;
    this.cdr.markForCheck();

    // Отправляем всю модель целиком. Все модели агента живут в одном
    // плоском списке model_versions (см. SKN-312) — тип каждой entry
    // определяется на фронте/бэке через ModelVersion.Model.ModelType.Code.
    // Здесь дропаем незаполненные placeholder'ы («Добавить модель» создаёт
    // пустой slot) и перепроставляем sort_index в порядке итогового
    // массива внутри своей группы.
    const rawEntries = this.allModelVersions().filter(v => !!v.model_version_id);
    const groupCounter = new Map<string, number>();
    // AgentModelVersionEntryInputDto — write-only DTO, snapshot
    // model_version в него не входит. Стриппаем перед отправкой (у нас
    // в state может быть настоящий бэковский snapshot или UI-stub от
    // placeholder-row'а, ни то ни другое бэку не нужно).
    const normalizedEntries: AgentModelVersionEntryInputDto[] = rawEntries.map(e => {
      const code = this.getEntryTypeCode(e) ?? 'unknown';
      const idx = groupCounter.get(code) ?? 0;
      groupCounter.set(code, idx + 1);
      const { model_version: _snapshot, ...input } = e;
      return { ...input, sort_index: idx };
    });

    const updateData: UpdateAgentCommand = {
      id: this.agent()!.id,
      title: this.agent()!.title,
      mode: this.agent()!.mode ?? 'Classic',
      system_prompt: this.agent()!.system_prompt,
      language_locales: this.agent()!.language_locales,
      disable_interruptions_during_first_message: this.agent()!.disable_interruptions_during_first_message,
      model_versions: normalizedEntries,
      variables_open_api_schema: this.agent()!.variables_open_api_schema,
      tools: this.agent()!.tools,
      is_published: this.agent()!.is_published,
      publish_options: this.agent()!.public_agent?.publish_options,
      default_phone_number_agent_id: this.agent()!.default_phone_number_agent_id ?? null
    };

    this.agentsService.apiAgentsPut({ body: updateData }).pipe(
      switchMap(result => this.agentsService.apiAgentsIdGet({ id: result.id! }))
    ).subscribe({
      next: updatedAgent => {
        this.agent.set(updatedAgent);
        this.sortLanguageLocales();
        this.publishAssistantContext();
        this.notificationService.showSuccess($localize`:@@agentEdit.notify.updated:Agent updated successfully`);
        this.isSaving = false;
        this.cdr.markForCheck();
        if (onSuccess) onSuccess();
      },
      error: (error: any) => {
        console.error('Error updating agent:', error);
        this.notificationService.showError($localize`:@@agentEdit.notify.updateFail:Failed to update agent`);
        this.isSaving = false;
        this.cdr.markForCheck();
        onError?.(error?.error?.detail ?? error?.message ?? 'Failed to update agent');
      }
    });
  }

  // Handle form field changes to trigger change detection
  onInstructionsChange(instructions: string): void {
    if (this.agent()) {
      this.agent()!.system_prompt = instructions;
      this.updateSystemPromptVariables(instructions);
      this.cdr.markForCheck();
    }
  }

  // Update dynamic variables from system prompt
  updateSystemPromptVariables(systemPrompt: string): void {
    this.systemPromptVariables = this.dynamicVariablesService.updateVariablesFromText(
      systemPrompt,
      this.agent()?.variables_open_api_schema
    );
  }

  // Handle variable changes from the dynamic-variables component
  onVariableChange(variable: PropertyDefinition): void {
    const index = this.systemPromptVariables.findIndex(v => v.name === variable.name);
    if (index >= 0) {
      this.systemPromptVariables[index] = variable;

      // Update agent's variables_open_api_schema
      if (this.agent()) {
        this.agent()!.variables_open_api_schema = this.dynamicVariablesService.propertiesToSchema(this.systemPromptVariables);
      }

      this.cdr.markForCheck();
    }
  }

  // Handle system prompt text changes from variables-manager
  onSystemPromptTextChange(text: string): void {
    if (this.agent()) {
      this.agent()!.system_prompt = text;
      this.updateSystemPromptVariables(text);
      this.cdr.markForCheck();
    }
  }

  // Handle variables schema changes from variables-manager
  onVariablesSchemaChange(schema: any): void {
    if (this.agent()) {
      this.agent.set({ ...this.agent()!, variables_open_api_schema: schema });
      this.cdr.markForCheck();
    }
  }

  onSystemPromptOptionSelect(optionValue: string): void {
    const option = this.primaryLanguageLocaleOptions.find(o => o.value === optionValue);
    if (option && this.agent()) {
      this.agent()!.system_prompt = option.system_prompt;
      this.cdr.markForCheck();
    }
  }

  onNameChange(name: string): void {
    if (this.agent()) {
      this.agent()!.title = name;
      this.cdr.markForCheck();
    }
  }

  onLanguageChange(): void {
    // publicAgentPreview зависит от agent().language_locales — bump после
    // add/remove/rename локали, иначе встроенный тестер во вкладке Talk
    // to agent видит устаревший popover выбора языка.
    this.bumpAgent();
    this.cdr.markForCheck();
  }



  private buildLlmTreeNodes(): void {
    if (!this.modelsHierarchy || !this.modelsHierarchy.model_types) {
      this.llmTreeNodes = [];
      return;
    }

    // Find LLM model type
    const llmModelType = this.modelsHierarchy.model_types.find(mt => mt.code === 'llm');
    if (!llmModelType || !llmModelType.gateways) {
      this.llmTreeNodes = [];
      return;
    }

    this.llmTreeNodes = llmModelType.gateways
      .map(gateway => {
        const children = this.buildLlmGatewayModels(gateway);

        if (children.length > 0) {
          return {
            key: gateway.id,
            label: gateway.name,
            data: `${gateway.name} Gateway`,
            icon: 'pi pi-fw pi-building',
            selectable: false,
            children: children
          };
        }
        return null;
      })
      .filter(gateway => gateway !== null);


    // After building tree nodes, set the selected value if we have model_version_id
    const primaryVersionId = this.primaryLlmVersionId;
    if (this.agent() && primaryVersionId) {
      this.setSelectedModelFromId(primaryVersionId);
    }
  }

  private buildLlmGatewayModels(gateway: GatewayHierarchy): any[] {
    if (!gateway.models) {
      return [];
    }

    return gateway.models.map(model => {
      const versions = this.buildModelVersionNodes(model);
      const label = model.author?.name ? `${model.author.name} ${model.name}` : (model.name || '');
      return {
        key: model.id,
        label,
        data: `${label} Model`,
        icon: 'pi pi-fw pi-cog',
        selectable: false,
        children: versions
      };
    });
  }

  private buildModelVersionNodes(model: ModelHierarchy): any[] {
    if (!model.versions) return [];

    return model.versions.map(version => ({
      key: version.id,
      label: `${model.name} ${version.name}`,
      data: `${model.name} ${version.name}`,
      icon: 'pi pi-fw pi-file',
      selectable: true // Only versions are selectable
    }));
  }

  onLlmModelVersionChange(): void {
    if (this.agent() && this.selectedLlmModelVersion) {
      // TreeSelect returns the selected node object, extract the key
      const modelVersionId = this.selectedLlmModelVersion.key;
      const chain = this.cloneActiveChain();
      this.ensureActivePrimary(chain).model_version_id = modelVersionId;
      this.replaceChain(this.activeCode(), chain);
      this.bumpAgentSignal();
    }
    this.cdr.markForCheck();
  }

  onSttModelVersionChange(): void {
    if (this.agent() && this.selectedSttModelVersion) {
      // TreeSelect returns the selected node object, extract the key
      const modelVersionId = this.selectedSttModelVersion.key;
      const previousVersionId = this.sttEntry()?.model_version_id;
      this.upsertSingletonEntry('stt', {
        model_version_id: modelVersionId,
        // Reset properties when switching to a different version;
        // PropertiesEdit re-initialises from schema defaults.
        ...(previousVersionId !== modelVersionId ? { additional_properties: [] } : {}),
      });
      this.bumpAgentSignal();
    }
    this.cdr.markForCheck();
  }

  // Universal method to get hierarchical schemas from any tree node.
  // Бэк отдаёт additional_properties как OpenApiPropertiesSchema (с array-форм
  // properties); PropertiesEdit ждёт плоский dict {name: descriptor} —
  // конвертим через descriptorEntriesToDict.
  /**
   * Walk the hierarchy and return the version with id === versionId and
   * its parent model. Universal lookup used by every model-config UI
   * (LLM / STT / TTS / VAD) — pick the right `*_version_id` off the agent.
   */
  private findModelAndVersion(
    versionId: string | null | undefined,
  ): { model: ModelHierarchy; version: ModelVersionHierarchy } | null {
    if (!versionId || !this.modelsHierarchy) return null;
    for (const type of this.modelsHierarchy.model_types ?? []) {
      for (const gateway of type.gateways ?? []) {
        for (const model of gateway.models ?? []) {
          const version = model.versions?.find(v => v.id === versionId);
          if (version) return { model, version };
        }
      }
    }
    return null;
  }

  /** ModelType.code (llm/realtime/stt/vad/tts) для entry. Читаем из
   *  snapshot'а `entry.model_version.model.model_type_code` — бэк кладёт
   *  его в AgentModelInfoDto (SKN-312 + snapshot enrichment). Fallback
   *  на lookup по id в modelsHierarchy — для entries чья модель не
   *  засветилась в snapshot (edge-case, обычно всегда есть). */
  private getEntryTypeCode(entry: AgentModelVersionEntryDto | null | undefined): string | null {
    if (!entry) return null;
    const fromSnapshot = entry.model_version?.model?.model_type_code;
    if (fromSnapshot) return fromSnapshot;
    return this.resolveTypeFromHierarchy(entry.model_version_id);
  }

  private resolveTypeFromHierarchy(versionId: string | null | undefined): string | null {
    if (!versionId || !this.modelsHierarchy) return null;
    for (const type of this.modelsHierarchy.model_types ?? []) {
      for (const gateway of type.gateways ?? []) {
        for (const model of gateway.models ?? []) {
          if (model.versions?.some(v => v.id === versionId)) {
            return type.code ?? null;
          }
        }
      }
    }
    return null;
  }

  /** ==== Agent.ModelVersions view (плоский список → сегменты по типу) ====
   *
   *  Backend схлопнул llm_versions/realtime_versions/stt/vad/tts+voice-per-locale
   *  в один Array<AgentModelVersionEntryDto> model_versions[]. Тип entry
   *  берём из snapshot: entry.model_version.model.model_type_code.
   *
   *  Пустая entry (создаётся кнопкой "+ Добавить модель" до выбора модели)
   *  тоже имеет snapshot-stub с проставленным model_type_code — тогда
   *  фильтр по типу её видит.
   *
   *  Reader'ы ниже строят сегментированные views (STT / VAD / LLM chain /
   *  Realtime chain / TTS entry per locale). Writer'ы мутируют один и тот
   *  же массив.
   */
  private allModelVersions(): AgentModelVersionEntryDto[] {
    const a = this.agent();
    if (!a) return [];
    if (!a.model_versions) a.model_versions = [];
    return a.model_versions!;
  }

  private entriesByType(code: string): AgentModelVersionEntryDto[] {
    return this.allModelVersions().filter(e => this.getEntryTypeCode(e) === code);
  }

  /** Snapshot-stub с проставленным типом — для пустой entry, у которой
   *  ещё нет валидного model_version_id. Даёт reader'ам возможность
   *  фильтровать её по типу до момента выбора модели. При выборе
   *  реальная entry подменит stub и snapshot приедет с бэка. */
  private makeTypeStub(code: string): AgentModelVersionDto {
    return { model: { model_type_code: code } };
  }

  private sttEntry(): AgentModelVersionEntryDto | undefined {
    return this.entriesByType('stt')[0];
  }
  private vadEntry(): AgentModelVersionEntryDto | undefined {
    return this.entriesByType('vad')[0];
  }
  private llmEntries(): AgentModelVersionEntryDto[] {
    return this.entriesByType('llm').sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
  }
  private realtimeEntries(): AgentModelVersionEntryDto[] {
    return this.entriesByType('realtime').sort((a, b) => (a.sort_index ?? 0) - (b.sort_index ?? 0));
  }
  private ttsEntryForLocale(localeId: string | null | undefined): AgentModelVersionEntryDto | undefined {
    if (!localeId) return undefined;
    return this.entriesByType('tts').find(e => e.agent_language_locale_id === localeId);
  }

  /** Snapshot'ы моделей, сохранённых на агенте по данному типу — для
   *  проброса в `[includeVersionSnapshots]` селектора. Гарантия что
   *  выбранная агентом модель отрисуется в списке, даже если бэк её
   *  сейчас не отдаёт в иерархии. */
  private snapshotsForType(code: string): AgentModelVersionDto[] {
    return this.entriesByType(code)
      .map(e => e.model_version)
      .filter((m): m is AgentModelVersionDto => !!m?.id);
  }
  private snapshotsForTtsLocale(localeId: string | null | undefined): AgentModelVersionDto[] {
    const entry = this.ttsEntryForLocale(localeId);
    return entry?.model_version?.id ? [entry.model_version] : [];
  }

  /** Snapshot'ы для sec-agent picker'а (все ряды активного каскада —
   *  LLM или Realtime). */
  readonly activeChainSnapshots = computed<AgentModelVersionDto[]>(() =>
    this.snapshotsForType(this.isRealtimeMode() ? 'realtime' : 'llm'),
  );

  /** Snapshot STT/VAD моделей — на случай что бэк не отдаёт retired-модель. */
  get sttSnapshots(): AgentModelVersionDto[] { return this.snapshotsForType('stt'); }
  get vadSnapshots(): AgentModelVersionDto[] { return this.snapshotsForType('vad'); }

  ttsSnapshotsForLocale(locale: AgentLanguageLocaleDto): AgentModelVersionDto[] {
    return this.snapshotsForTtsLocale(locale.id);
  }

  /** Заменяет единственную entry типа `code` (STT/VAD). Пустой versionId
   *  удаляет запись. Возвращает мутированный список — bumpAgentSignal
   *  вызывать вручную. */
  private upsertSingletonEntry(
    code: 'stt' | 'vad',
    patch: Partial<AgentModelVersionEntryDto> & { model_version_id: string | undefined | null },
  ): void {
    const list = this.allModelVersions();
    const idx = list.findIndex(e => this.getEntryTypeCode(e) === code);
    if (!patch.model_version_id) {
      if (idx >= 0) list.splice(idx, 1);
      return;
    }
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch, model_version_id: patch.model_version_id };
    } else {
      list.push({
        sort_index: 0,
        additional_properties: [],
        temperature: null,
        ...patch,
        model_version_id: patch.model_version_id,
      });
    }
  }

  /** Upsert TTS entry (per-locale). Пустой versionId — удаляет запись. */
  private upsertTtsForLocale(
    localeId: string,
    patch: Partial<AgentModelVersionEntryDto> & { model_version_id: string | undefined | null },
  ): void {
    const list = this.allModelVersions();
    const idx = list.findIndex(
      e => this.getEntryTypeCode(e) === 'tts' && e.agent_language_locale_id === localeId,
    );
    if (!patch.model_version_id) {
      if (idx >= 0) list.splice(idx, 1);
      return;
    }
    if (idx >= 0) {
      list[idx] = { ...list[idx], ...patch, model_version_id: patch.model_version_id, agent_language_locale_id: localeId };
    } else {
      list.push({
        sort_index: 0,
        additional_properties: [],
        temperature: null,
        ...patch,
        model_version_id: patch.model_version_id,
        agent_language_locale_id: localeId,
      });
    }
  }

  /** Полностью пересобирает цепочку entries данного типа (LLM или
   *  Realtime — activeVersions), проставляя sort_index в порядке массива. */
  private replaceChain(code: 'llm' | 'realtime', chain: AgentModelVersionEntryDto[]): void {
    const list = this.allModelVersions();
    const kept = list.filter(e => this.getEntryTypeCode(e) !== code);
    kept.push(...chain.map((e, i) => ({ ...e, sort_index: i })));
    const a = this.agent();
    if (a) a.model_versions = kept;
  }

  /**
   * Read additional_properties off the resolved version and feed them
   * to the editor. Dict conversion is the boundary with the legacy
   * `app-properties-edit` (it consumes dict-shape schemas). Model-level
   * additional_properties no longer participate — the schema lives on
   * the version exclusively.
   */
  private getSchemasForVersion(_versionId: string | null | undefined): Array<{ [key: string]: any }> {
    // SKN-313: additional_properties больше не приходят в hierarchy.
    // Полная схема грузится тестером через GET /api/Models/versions/{id}
    // при открытии drawer'а — agent-edit больше не отвечает за эту склейку.
    // Возвращаем пустой массив, чтобы app-properties-edit не рисовал
    // fields до момента когда caller подхватит fresh schema.
    return [];
  }

  getLlmSchemas(): Array<{ [key: string]: any }> {
    return this.getSchemasForVersion(this.primaryLlmVersionId);
  }

  hasLlmProperties(): boolean {
    return this.getLlmSchemas().some(s => s && Object.keys(s).length > 0);
  }

  getLlmValues(): { [key: string]: any } {
    return valueEntriesToDict(this.primaryLlm?.additional_properties);
  }

  onLlmPropertiesChange(values: { [key: string]: any }): void {
    if (!this.agent()) return;
    const chain = this.cloneActiveChain();
    this.ensureActivePrimary(chain).additional_properties = dictToValueEntries(values);
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  get sttVersionId(): string | undefined {
    return this.sttEntry()?.model_version_id ?? undefined;
  }
  get sttSnapshot(): AgentModelVersionDto | null | undefined {
    return this.sttEntry()?.model_version;
  }
  get vadVersionId(): string | undefined {
    return this.vadEntry()?.model_version_id ?? undefined;
  }
  get vadSnapshot(): AgentModelVersionDto | null | undefined {
    return this.vadEntry()?.model_version;
  }

  getSttSchemas(): Array<{ [key: string]: any }> {
    return this.getSchemasForVersion(this.sttEntry()?.model_version_id);
  }

  hasSttProperties(): boolean {
    return this.getSttSchemas().some(s => s && Object.keys(s).length > 0);
  }

  getSttValues(): { [key: string]: any } {
    return valueEntriesToDict(this.sttEntry()?.additional_properties);
  }

  getVadValues(): { [key: string]: any } {
    return valueEntriesToDict(this.vadEntry()?.additional_properties);
  }

  onSttPropertiesChange(values: { [key: string]: any }): void {
    if (!this.agent()) return;
    const entry = this.sttEntry();
    if (!entry) return;
    this.upsertSingletonEntry('stt', {
      model_version_id: entry.model_version_id!,
      additional_properties: dictToValueEntries(values),
    });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  // ---- TTS per-locale additional properties ----
  // TTS-настройки храним per-locale (язык/локаль) как отдельные entries
  // в agent.model_versions с agent_language_locale_id = locale.id.

  getTtsSchemas(locale: AgentLanguageLocaleDto): Array<{ [key: string]: any }> {
    return this.getSchemasForVersion(this.ttsEntryForLocale(locale.id)?.model_version_id);
  }

  hasTtsProperties(locale: AgentLanguageLocaleDto): boolean {
    return this.getTtsSchemas(locale).some(s => s && Object.keys(s).length > 0);
  }

  getTtsValues(locale: AgentLanguageLocaleDto): { [key: string]: any } {
    return valueEntriesToDict(this.ttsEntryForLocale(locale.id)?.additional_properties);
  }

  /** Compat helpers для HTML — вытягивают поля из TTS entry per-locale
   *  (agent.model_versions с agent_language_locale_id === locale.id). */
  getTtsVersionIdForLocale(locale: AgentLanguageLocaleDto): string | undefined {
    return this.ttsEntryForLocale(locale.id)?.model_version_id ?? undefined;
  }
  getTtsSnapshotForLocale(locale: AgentLanguageLocaleDto): AgentModelVersionDto | null | undefined {
    return this.ttsEntryForLocale(locale.id)?.model_version;
  }
  getVoiceIdForLocale(locale: AgentLanguageLocaleDto): string | null | undefined {
    return this.ttsEntryForLocale(locale.id)?.voice_id;
  }

  onTtsPropertiesChange(locale: AgentLanguageLocaleDto, values: { [key: string]: any }): void {
    if (!locale.id) return;
    const entry = this.ttsEntryForLocale(locale.id);
    if (!entry) return;
    this.upsertTtsForLocale(locale.id, {
      model_version_id: entry.model_version_id!,
      additional_properties: dictToValueEntries(values),
    });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  private setSelectedSttModelFromId(modelVersionId: string): void {
    // Find the actual node object in the tree
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNodeByKey(this.sttTreeNodes, modelVersionId);
    if (foundNode) {
      this.selectedSttModelVersion = foundNode;
      this.cdr.markForCheck();
    } else {
      this.selectedSttModelVersion = null;
    }
  }

  private setSelectedModelFromId(modelVersionId: string): void {

    // Find the actual node object in the tree
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };
    
    const foundNode = findNodeByKey(this.llmTreeNodes, modelVersionId);
    if (foundNode) {
      this.selectedLlmModelVersion = foundNode;
      this.cdr.markForCheck(); // Force change detection
    } else {
      this.selectedLlmModelVersion = null;
    }
  }

  // Build language options from API data (using flat language-locales)
  private buildLanguageOptions(): void {
    if (!this.languagesData || !this.languagesData.language_locales) {
      this.languageOptions = [];
      return;
    }

    const options: any[] = [];

    // Iterate through flat language-locale combinations
    this.languagesData.language_locales.forEach(ll => {
      if (ll.full_code) {
        options.push({
          label: `${ll.locale_flag || ''} ${ll.language_name}`,
          value: ll.full_code,
          languageCode: ll.language_code,
          localeName: ll.locale_name,
          nativeLocaleName: ll.locale_native_name,
          languageName: ll.language_name,
          flag: ll.locale_flag,
          isDefault: ll.is_default,
          disabled: !ll.is_active
        });
      }
    });

    // Sort by default locales first, then alphabetically
    this.languageOptions = options.sort((a, b) => {
      if (a.isDefault && !b.isDefault) return -1;
      if (!a.isDefault && b.isDefault) return 1;
      return a.label.localeCompare(b.label);
    });
  }

  // Build TTS Tree Nodes (similar to LLM but for TTS models)
  private buildSttTreeNodes(): void {
    if (!this.modelsHierarchy || !this.modelsHierarchy.model_types) {
      this.sttTreeNodes = [];
      return;
    }

    // Find STT model type
    const sttModelType = this.modelsHierarchy.model_types.find(mt => mt.code === 'stt');
    if (!sttModelType || !sttModelType.gateways) {
      this.sttTreeNodes = [];
      return;
    }

    this.sttTreeNodes = sttModelType.gateways
      .map(gateway => {
        const children = this.buildSttGatewayModels(gateway);

        if (children.length > 0) {
          return {
            key: gateway.id,
            label: gateway.name,
            data: `${gateway.name} Gateway`,
            icon: 'pi pi-fw pi-building',
            selectable: false,
            children: children
          };
        }
        return null;
      })
      .filter(gateway => gateway !== null);
  }

  private buildSttGatewayModels(gateway: GatewayHierarchy): any[] {
    if (!gateway.models) {
      return [];
    }

    return gateway.models.map(model => {
      const versions = this.buildSttModelVersionNodes(model);
      const label = model.author?.name ? `${model.author.name} ${model.name}` : (model.name || '');
      return {
        key: model.id,
        label,
        data: {
          type: 'model',
          ...model
        },
        icon: 'pi pi-fw pi-microphone',
        selectable: false,
        children: versions
      };
    });
  }

  private buildSttModelVersionNodes(model: ModelHierarchy): any[] {
    if (!model.versions) return [];

    return model.versions.map(version => ({
      key: version.id,
      label: `${model.name} ${version.name}`,
      data: {
        type: 'version',
        ...version,  // Include all version data including additional_properties
        modelId: model.id,  // Add reference to parent model
        modelData: model  // Include parent model data for easy access
      },
      icon: 'pi pi-fw pi-file',
      selectable: true, // Only versions are selectable
      partialSelected: false
    }));
  }

  private buildTtsTreeNodes(): void {
    if (!this.modelsHierarchy || !this.modelsHierarchy.model_types) {
      this.ttsTreeNodes = [];
      return;
    }

    // Find TTS model type
    const ttsModelType = this.modelsHierarchy.model_types.find(mt => mt.code === 'tts');
    if (!ttsModelType || !ttsModelType.gateways) {
      this.ttsTreeNodes = [];
      return;
    }

    this.ttsTreeNodes = ttsModelType.gateways
      .map(gateway => {
        const children = this.buildTtsGatewayModels(gateway);

        if (children.length > 0) {
          return {
            key: gateway.id,
            label: gateway.name,
            data: `${gateway.name} Gateway`,
            icon: 'pi pi-fw pi-building',
            selectable: false,
            children: children
          };
        }
        return null;
      })
      .filter(gateway => gateway !== null);

    // Очищаем кэш фильтрованных узлов при пересборке
    this.filteredTtsNodes = {};
  }

  private buildTtsGatewayModels(gateway: GatewayHierarchy): any[] {
    if (!gateway.models) {
      return [];
    }

    return gateway.models.map(model => {
      const versions = this.buildTtsModelVersionNodes(model);
      const label = model.author?.name ? `${model.author.name} ${model.name}` : (model.name || '');
      return {
        key: model.id,
        label,
        data: `${label} Model`,
        icon: 'pi pi-fw pi-volume-up',
        selectable: false,
        children: versions
      };
    });
  }

  private buildTtsModelVersionNodes(model: ModelHierarchy): any[] {
    if (!model.versions) return [];

    return model.versions.map(version => ({
      key: version.id,
      label: `${model.name} ${version.name}`,
      data: `${model.name} ${version.name}`,
      icon: 'pi pi-fw pi-file',
      selectable: true, // Only versions are selectable
      partialSelected: false
    }));
  }

  private setSelectedTtsModelFromId(modelVersionId: string): void {
    // Find the actual node object in the tree
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };

    const foundNode = findNodeByKey(this.ttsTreeNodes, modelVersionId);
    if (foundNode) {
      this.selectedTtsModelVersion = foundNode;
      this.cdr.markForCheck();
    } else {
      this.selectedTtsModelVersion = null;
    }
  }

  // Voice change handlers
  onVoiceLanguageChange(): void {
    // Logic for when voice language changes
    this.cdr.markForCheck();
  }

  // Legacy method removed - now using onPrimaryTtsModelChange() and onAdditionalTtsModelChange()

  // Get display name for language from language options
  getLanguageDisplayName(languageCode: string): string {
    const option = this.languageOptions.find(opt => opt.value === languageCode);
    return option?.label || languageCode;
  }

  // Get TTS tree nodes filtered by language support
  getTtsTreeNodesForLanguage(languageCode: string): any[] {
    if (!this.ttsTreeNodes || !languageCode) {
      return [];
    }

    // Используем кэш если уже вычисляли для этого языка
    if (this.filteredTtsNodes[languageCode]) {
      return this.filteredTtsNodes[languageCode];
    }

    const filteredNodes = this.filterTtsTreeByLanguage(this.ttsTreeNodes, languageCode);

    // Кэшируем результат
    this.filteredTtsNodes[languageCode] = filteredNodes;

    return filteredNodes;
  }

  // Методы для конкретных языков (для использования в template)
  getPrimaryLanguageTtsNodes(): any[] {
    const primaryLang = this.getPrimaryLanguage();
    if (!primaryLang) return [];
    return this.getTtsTreeNodesForLanguage(primaryLang);
  }

  private filterTtsTreeByLanguage(nodes: any[], languageCode: string): any[] {
    const result: any[] = [];

    for (const providerNode of nodes) {
      if (!providerNode.children || providerNode.children.length === 0) {
        continue;
      }

      const filteredModels: any[] = [];

      for (const modelNode of providerNode.children) {
        if (!modelNode.children || modelNode.children.length === 0) {
          continue;
        }

        const supportedVersions: any[] = [];

        for (const versionNode of modelNode.children) {
          if (this.doesTtsVersionSupportLanguage(versionNode.key, languageCode)) {
            supportedVersions.push(versionNode);
          }
        }

        if (supportedVersions.length > 0) {
          filteredModels.push({
            ...modelNode,
            children: supportedVersions
          });
        }
      }

      if (filteredModels.length > 0) {
        result.push({
          ...providerNode,
          children: filteredModels
        });
      }
    }

    return result;
  }

  private filterTreeNodesByLanguage(nodes: any[], languageCode: string): any[] {
    return nodes.map(node => {
      if (node.children && node.children.length > 0) {
        const filteredChildren = this.filterTreeNodesByLanguage(node.children, languageCode);
        if (filteredChildren.length > 0) {
          return {
            ...node,
            children: filteredChildren
          };
        }
        return null;
      } else if (node.selectable) {
        // This is a version node - check if it supports the language
        const versionId = node.key;
        if (this.doesTtsVersionSupportLanguage(versionId, languageCode)) {
          return node;
        }
        return null;
      }
      return node;
    }).filter(node => node !== null);
  }

  private doesTtsVersionSupportLanguage(versionId: string, languageCode: string): boolean {
    // Language filtering removed - all TTS versions support all languages now
    return true;
  }

  // Primary TTS model handlers
  onPrimaryTtsModelChange(selectedNode: any): void {
    this.primaryTtsModel = selectedNode;

    // Save to agent's primary language locale (TTS entry с
    // agent_language_locale_id = локали.id).
    const primaryLocale = this.primaryLanguageLocale;
    if (primaryLocale?.id && selectedNode?.key) {
      this.upsertTtsForLocale(primaryLocale.id, { model_version_id: selectedNode.key });
      this.bumpAgentSignal();
    }

    this.cdr.markForCheck();
  }

  // Additional TTS model handlers
  getAdditionalTtsModel(languageCode: string): any {
    // First check if we have cached value
    if (this.additionalTtsModels[languageCode]) {
      return this.additionalTtsModels[languageCode];
    }

    // Try to find the model from agent's language_locales
    const locale = this.findLocaleByFullCode(languageCode);
    if (!locale) return null;

    const agentLocale = this.agent()?.language_locales?.find(
      ll => ll.language_locale_id === locale.id && !ll.is_default
    );

    if (agentLocale) {
      // Get effective TTS version ID (user selection or default)
      const effectiveTtsVersionId = this.getEffectiveTtsVersionIdForLocale(agentLocale);

      if (effectiveTtsVersionId) {
        // Find and cache the TTS model node
        const findNodeByKey = (nodes: any[], key: string): any => {
          for (const node of nodes) {
            if (node.key === key) return node;
            if (node.children) {
              const found = findNodeByKey(node.children, key);
              if (found) return found;
            }
          }
          return null;
        };

        const ttsNodes = this.getTtsTreeNodesForLanguage(languageCode);
        const foundNode = findNodeByKey(ttsNodes, effectiveTtsVersionId);
        if (foundNode) {
          this.additionalTtsModels[languageCode] = foundNode;
          return foundNode;
        }
      }
    }

    return null;
  }

  onAdditionalTtsModelChange(languageCode: string, selectedNode: any): void {
    // Cache the selection
    if (selectedNode) {
      this.additionalTtsModels[languageCode] = selectedNode;
    } else {
      delete this.additionalTtsModels[languageCode];
    }

    // Save to agent's language locale (TTS entry per-locale в
    // agent.model_versions[]).
    const locale = this.findLocaleByFullCode(languageCode);
    if (!locale) return;

    const agentLocale = this.agent()?.language_locales?.find(
      ll => ll.language_locale_id === locale.id && !ll.is_default
    );

    if (agentLocale?.id) {
      this.upsertTtsForLocale(agentLocale.id, { model_version_id: selectedNode?.key });
      this.bumpAgentSignal();
    }

    this.cdr.markForCheck();
  }

  // Language management
  removeAdditionalLanguage(languageCode: string): void {
    if (!this.agent()?.language_locales) return;

    // Find and remove the language locale
    const locale = this.findLocaleByFullCode(languageCode);
    if (!locale) return;

    this.agent()!.language_locales = this.agent()!.language_locales!.filter(
      ll => ll.language_locale_id !== locale.id || ll.is_default
    );
    delete this.additionalTtsModels[languageCode];
    this.updateAvailableLanguagesMenu();
    this.cdr.markForCheck();
  }

  trackByLanguage(_index: number, langCode: string): string {
    return langCode;
  }

  // Initialize voice configuration
  private initializeVoiceConfiguration(): void {
    if (!this.agent()) return;

    // Инициализируем TTS селекторы как пустые (пока без привязки к агенту)
    this.primaryTtsModel = null;

    // Initialize additional TTS models (placeholder for now)
    this.additionalTtsModels = {};
  }




  // STT functionality will be implemented later through Models hierarchy or separate service

  // Temperature is now per-LLM-row and edited inside the model-tester
  // drawer (with its own enable/disable toggle); the agent-edit form no
  // longer renders a top-level slider or preset buttons. The methods
  // below are kept as no-ops for backward compat in case any template
  // binding still references them — safe to delete once the form
  // refactor lands across every flow.
  updateTemperaturePresetsState(): void { /* no-op */ }
  setTemperaturePreset(_presetKey: 'deterministic' | 'creative' | 'moreCreative'): void { /* no-op */ }
  onTemperatureChange(): void { /* no-op */ }

  // Top panel action methods
  testAiAgent(): void {
    if (this.agent()?.id) {
      this.router.navigate(['/agents', this.agent()!.id, 'test']);
    }
  }

  saveAndTestAgent(): void {
    // Сначала save, потом переключение на Talk. Тестер живёт в БД:
    // при dispatch'е бэк читает system_prompt / LLM / STT / TTS / tools
    // из последней сохранённой версии, а не из UI-стейта. Без save юзер
    // тестировал бы «предыдущего» агента — сюрприз.
    // При ошибке save (валидация / сеть) saveAgent сам покажет уведомление
    // и НЕ дёрнет callback — вкладку не переключаем, юзер остаётся править.
    this.saveAgent(() => this.setActiveTab('talk'));
  }

  openSdkDocs(): void {
    const url = `${environment.docsUrl.replace(/\/$/, '')}/en/api`;
    window.open(url, '_blank', 'noopener');
  }

  /** URL для ссылки «Почитайте, какие LLM поддерживаются». Getter, чтобы
   *  использовать в HTML как обычный <a href> вместо кнопки-эмулятора. */
  get llmDocsUrl(): string {
    return `${environment.docsUrl.replace(/\/$/, '')}/features/agents`;
  }

  openConversationHistory(): void {
    this.router.navigate(['/conversations'], { queryParams: { agentId: this.agent()?.id } });
  }

  editAgentName(): void {
    // TODO: Implement edit agent name functionality
    this.notificationService.showInfo($localize`:@@agentEdit.notify.editNameSoon:Edit agent name feature coming soon`);
  }

  copyAgentId(): void {
    if (this.agent() && this.agent()!.id) {
      navigator.clipboard.writeText(this.agent()!.id!).then(() => {
        this.notificationService.showSuccess($localize`:@@agentEdit.notify.idCopied:Agent ID copied to clipboard`);
      }).catch(err => {
        console.error('Failed to copy agent ID:', err);
        this.notificationService.showError($localize`:@@agentEdit.notify.idCopyFail:Failed to copy agent ID`);
      });
    }
  }

  duplicateAgent(): void {
    if (!this.agent()?.id) return;

    // Navigate to duplicate route
    this.router.navigate(['/agents', this.agent()!.id, 'duplicate']);
  }

  deleteAgent(): void {
    this.showDeleteConfirmation = true;
  }

  onDeleteConfirm(): void {
    if (!this.agent()?.id) {
      this.notificationService.showError($localize`:@@agentEdit.notify.noAgentToDelete:No agent to delete`);
      return;
    }

    this.isDeletingAgent = true;
    this.cdr.markForCheck();

    this.agentsService.apiAgentsIdDelete({ id: this.agent()!.id! }).subscribe({
      next: () => {
        this.notificationService.showSuccess($localize`:@@agents.delete.success:Agent deleted successfully`);
        this.showDeleteConfirmation = false;
        this.isDeletingAgent = false;
        // Navigate back to agents list
        this.router.navigate(['/agents']);
      },
      error: (error) => {
        console.error('Failed to delete agent:', error);
        this.notificationService.showError($localize`:@@agents.delete.error:Failed to delete agent`);
        this.isDeletingAgent = false;
        this.cdr.markForCheck();
      }
    });
  }

  onDeleteCancel(): void {
    this.showDeleteConfirmation = false;
    this.cdr.markForCheck();
  }

  /**
   * Update custom tools menu with real webhook tools from API
   */
  // private updateCustomToolsMenu(): void {
  //   this.customToolsMenuItems = [];
      
  //     // Add separator if we have tools
  //     this.customToolsMenuItems.push({
  //       separator: true
  //     });
    
  //   // Always show static menu options
  //   this.customToolsMenuItems.push(
  //     {
  //       label: 'Add client tool',
  //       icon: 'pi pi-plus-circle',
  //       disabled: true
  //     }
  //     // {
  //     //   label: 'Create new webhook tool',
  //     //   icon: 'pi pi-plus',
  //     //   command: () => this.addWebhookTool()
  //     // }
  //   );
  // }

  private initializeBreadcrumb(): void {
    this.breadcrumbItems = [
      {
        label: $localize`:@@nav.agents:Agents`,
        routerLink: '/agents'
      },
      {
        label: this.agent()?.title || $localize`:@@agentEdit.breadcrumb.fallback:Agent Edit`
      }
    ];
  }

  goBack(): void {
    this.router.navigate(['/agents']);
  }

  // Custom tools methods
  addCustomTool(toolName: string): void {
    // TODO: Implement add custom tool functionality
    this.notificationService.showInfo($localize`:@@agentEdit.notify.addToolSoon:Adding custom tool: ${toolName}:toolName: (feature coming soon)`);
  }

  removeToolFromAgent(toolId: string): void {
    const current = this.agent();
    if (!current?.tools) return;

    // Через set, а не присваиванием в agent()!.tools: список webhook-тулов
    // рисуется из computed connectedTools, а тот пересчитывается только на
    // смену ССЫЛКИ сигнала. Правка поля внутри объекта её не меняет — тул
    // исчезал из данных, но оставался на экране, и markForCheck не помогал:
    // computed отдавал запомненное значение.
    this.agent.set({
      ...current,
      tools: current.tools.filter(t => t.tool_id !== toolId),
    });

    // Update menu to reflect changes
    this.updateCustomToolsMenu();

    this.cdr.markForCheck();
  }

  addToolToAgent(toolId: string, customDescription?: string): void {
    const current = this.agent();
    if (!current) return;

    const tools = current.tools ?? [];

    // Add tool if not already present
    if (!tools.some(t => t.tool_id === toolId)) {
      const agentTool: AgentToolDto = {
        tool_id: toolId,
        custom_description: customDescription || null
      };
      // Через set с НОВЫМ массивом, а не push в существующий: список webhook-
      // тулов рисуется из computed connectedTools, а тот пересчитывается
      // только на смену ССЫЛКИ сигнала. push её не менял — тул появлялся в
      // данных и уезжал на бэк при сохранении, но на экране не возникал.
      this.agent.set({ ...current, tools: [...tools, agentTool] });
    } else if (!current.tools) {
      this.agent.set({ ...current, tools: [] });
    }

    // Update menu to reflect changes
    this.updateCustomToolsMenu();

    this.cdr.markForCheck();
  }

  // Webhook drawer methods
  editTool(toolId: string): void {
    this.selectedToolId = toolId;
    this.showWebhookDrawer = true;
    this.cdr.markForCheck();
  }

  createNewWebhookTool(): void {
    // Open drawer for creating new tool
    this.selectedToolId = null;
    this.showWebhookDrawer = true;
    this.cdr.markForCheck();
  }

  onWebhookToolSaved(toolData?: any): void {
    
    // If this was creating a new tool (selectedToolId was null), add it to agent
    if (!this.selectedToolId && toolData && toolData.id && this.agent()) {
      this.addToolToAgent(toolData.id);

      // Also add the new tool to allTools so it appears in the connected tools list
      const current = this.allTools();
      if (current?.tools) {
        const newTool: ToolSummary = {
          id: toolData.id,
          title: toolData.title || 'New Tool',
          description: toolData.description || '',
          type: 'Webhook',
          created_by_email: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        // Новый объект, а не push в существующий массив: computed'ы выше
        // сравнивают ссылку и мутацию на месте не заметили бы.
        this.allTools.set({ ...current, tools: [...current.tools, newTool] });

        // Update the custom tools menu to reflect the changes
        this.updateCustomToolsMenu();
      }
    }
    
    // Close drawer and refresh data
    this.showWebhookDrawer = false;
    this.selectedToolId = null;
    
    // Tools will be refreshed automatically on next load
    
    this.cdr.markForCheck();
  }

  onWebhookToolCancelled(): void {
    // Just close drawer
    this.showWebhookDrawer = false;
    this.selectedToolId = null;
    this.cdr.markForCheck();
  }

  // System Tool drawer methods
  editSystemTool(tool: ToolSummary): void {
    this.selectedSystemTool = tool;
    this.showSystemToolDrawer = true;
    this.cdr.markForCheck();
  }






  // Get agent tool for system tool
  getAgentTool(toolId: string): AgentToolDto | undefined {
    return this.agent()?.tools?.find(t => t.tool_id === toolId);
  }

  // System tool event handlers
  onSystemToolSaved(agentTool: AgentToolDto): void {
    if (!this.agent()?.tools) return;

    const existingIndex = this.agent()!.tools!.findIndex(t => t.tool_id === agentTool.tool_id);

    if (existingIndex !== -1) {
      // Update existing tool
      this.agent()!.tools![existingIndex] = agentTool;
    } else {
      // Add new tool
      this.agent()!.tools!.push(agentTool);
    }

    // Close drawer
    this.showSystemToolDrawer = false;
    this.selectedSystemTool = null;
    this.cdr.markForCheck();
  }

  onSystemToolCancelled(): void {
    this.showSystemToolDrawer = false;
    this.selectedSystemTool = null;
    this.cdr.markForCheck();
  }

  // addWebhookTool(): void {
  //   // Reset selected tool for new tool
  //   this.selectedWebhookTool = this.createEmptyTool();
  //   this.isEditingWebhookTool = false;
  //   // Show sidebar
  //   this.showWebhookSidebar = true;
  //   this.cdr.markForCheck();
  // }

  // editWebhookTool(tool: HttpTool): void {
  //   // Convert old HttpTool to new CreateToolCommand format
  //   this.selectedWebhookTool = {
  //     title: tool.name,
  //     description: tool.description,
  //     method: tool.method as any,
  //     url: tool.endpoint,
  //     response_timeout_seconds: tool.timeout,
  //     headers: tool.headers?.map(h => ({ name: h.name, value: h.value })) || [],
  //     query_parameters: [],
  //     body_parameters: [],
  //     disable_interruptions: false
  //   };
  //   this.isEditingWebhookTool = true;
  //   // Show sidebar
  //   this.showWebhookSidebar = true;
  //   this.cdr.markForCheck();
  // }

  // addExistingWebhookTool(tool: HttpToolAPI): void {
  //   // Convert API tool to HttpTool format and set for editing
  //   this.selectedWebhookTool = {
  //     name: tool.name,
  //     description: tool.description || '',
  //     method: (tool.method || 'GET') as any,
  //     endpoint: tool.endpoint,
  //     timeout: tool.timeout || 30,
  //     headers: tool.headers || [],
  //     url_parameters: tool.url_parameters || [],
  //     payload: tool.payload || '',
  //     dynamic_variables: tool.dynamic_variables || []
  //   };
  //   this.isEditingWebhookTool = false; // Adding existing, not editing
  //   // Show sidebar
  //   this.showWebhookSidebar = true;
  //   this.cdr.markForCheck();
  // }

  // private createEmptyTool(): CreateToolCommand {
  //   return {
  //     title: '',
  //     description: '',
  //     method: 'GET',
  //     url: '',
  //     response_timeout_seconds: 30,
  //     headers: [],
  //     query_parameters: [],
  //     body_parameters: [],
  //     disable_interruptions: false
  //   };
  // }

  // Default Tools toggle methods
  onDefaultToolToggle(toolKey: string, enabled: boolean): void {
    if (toolKey === 'endCall') {
      this.defaultTools.endCall.enabled = enabled;
    } else if (toolKey === 'transferToAgent') {
      this.defaultTools.transferToAgent.enabled = enabled;
    }
    this.cdr.markForCheck();
  }

  // System Tools toggle methods
  onSystemToolToggle(toolId: string, enabled: boolean): void {
    if (!this.agent()) return;

    // Initialize tools array if it doesn't exist
    if (!this.agent()!.tools) {
      this.agent()!.tools = [];
    }

    if (enabled) {
      // Add system tool if not already present
      const alreadyExists = this.agent()!.tools!.some(t => t.tool_id === toolId);
      if (!alreadyExists) {
        const agentTool: AgentToolDto = {
          tool_id: toolId,
          custom_description: null,
          custom_disable_interruptions: null
        };
        this.agent()!.tools!.push(agentTool);
      }

      // Automatically open settings when enabling
      const entry = this.systemTools().find(t => t.tool.id === toolId);
      if (entry) {
        this.editSystemTool(entry.tool);
      }
    } else {
      // Remove system tool
      this.agent()!.tools = this.agent()!.tools!.filter(t => t.tool_id !== toolId);
    }

    this.cdr.markForCheck();
  }

  // // Webhook component event handlers
  // onWebhookToolSaved(_tool: CreateToolCommand): void {
  //   // Tool has already been saved to API by webhook-tool component
  //   // Just close the sidebar
  //   this.showWebhookSidebar = false;
  //   this.selectedWebhookTool = this.createEmptyTool();
  //   this.isEditingWebhookTool = false;
  //   this.cdr.markForCheck();
  // }

  // onWebhookToolCancelled(): void {
  //   // Hide sidebar without saving
  //   this.showWebhookSidebar = false;
  //   this.selectedWebhookTool = this.createEmptyTool();
  //   this.isEditingWebhookTool = false;
  //   this.cdr.markForCheck();
  // }

  // private updateAgentHttpTools(tools: HttpTool[], action: string): void {
  //   if (!this.agent()) return;

  //   const updateData = {
  //     http_tools: tools
  //   };

  //   this.agentsService.updateAgent(this.agent()!.id, updateData).subscribe({
  //     next: (updatedAgent) => {
  //       this.agent.set(updatedAgent);
  //       this.webhookTools = updatedAgent.http_tools || [];
  //       this.updateCustomToolsMenu();
        
  //       const toolName = tools[tools.length - 1].name; // Get the last tool name
  //       this.notificationService.showSuccess(`Webhook tool "${toolName}" ${action} successfully`);
        
  //       this.showWebhookSidebar = false;
  //       this.selectedWebhookTool = this.createEmptyTool();
  //       this.isEditingWebhookTool = false;
  //       this.cdr.markForCheck();
  //     },
  //     error: (error) => {
  //       console.error('Error saving webhook tool:', error);
  //       this.notificationService.showError('Failed to save webhook tool');
  //       this.cdr.markForCheck();
  //     }
  //   });
  // }

  // deleteWebhookTool(tool: HttpTool): void {
  //   if (!this.agent()) return;

  //   const updatedTools = this.webhookTools.filter(t => t.name !== tool.name);
  //   
  //   const updateData = {
  //     http_tools: updatedTools
  //   };

  //   this.agentsService.updateAgent(this.agent()!.id, updateData).subscribe({
  //     next: (updatedAgent) => {
  //       this.agent.set(updatedAgent);
  //       this.webhookTools = updatedAgent.http_tools || [];
  //       this.updateCustomToolsMenu();
  //       this.notificationService.showSuccess(`Webhook tool "${tool.name}" deleted successfully`);
  //       this.cdr.markForCheck();
  //     },
  //     error: (error) => {
  //       console.error('Error deleting webhook tool:', error);
  //       this.notificationService.showError('Failed to delete webhook tool');
  //       this.cdr.markForCheck();
  //     }
  //   });
  // }

  // Helper method to get selected model label
  getSelectedModelLabel(node: any): string {
    return node?.label || '';
  }

  // Helper methods to find tree nodes by version ID
  private findLlmNodeByVersionId(versionId: string): any {
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children?.length > 0) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };
    return findNodeByKey(this.llmTreeNodes, versionId);
  }

  private findSttNodeByVersionId(versionId: string): any {
    const findNodeByKey = (nodes: any[], key: string): any => {
      for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children?.length > 0) {
          const found = findNodeByKey(node.children, key);
          if (found) return found;
        }
      }
      return null;
    };
    return findNodeByKey(this.sttTreeNodes, versionId);
  }

  /** Gateway codes that switch the LLM picker / tester into custom-LLM
   *  mode (the user defines headers + body OpenAPI schemas in the test
   *  drawer). Centralised here so backend renames stay 1-spot to fix. */
  readonly customLlmGatewayCodes: string[] = ['custom', 'custom-llm'];

  // ===== LLM versions (каскад моделей) =====
  // Хранятся в общем плоском agent.model_versions[]; фильтруем по
  // ModelType.Code === 'llm' и сортируем по sort_index. Деления на
  // основную и fallback-модели нет — порядок в списке и есть приоритет
  // перебора. Compat-getters ниже используются в TS-инициализации
  // (HTML работает через active-*).
  readonly MAX_LLM_CASCADE = 3;

  get primaryLlm(): AgentModelVersionEntryDto | undefined {
    return this.llmEntries()[0];
  }

  get primaryLlmVersionId(): string {
    return this.primaryLlm?.model_version_id ?? '';
  }

  get primaryLlmSnapshot(): AgentModelVersionDto | null | undefined {
    return this.primaryLlm?.model_version;
  }

  get primaryLlmAdditionalProps(): OpenApiPropertyValueEntry[] {
    return this.primaryLlm?.additional_properties ?? [];
  }

  get primaryLlmTemperature(): number | null {
    return this.primaryLlm?.temperature ?? null;
  }

  getLlmRowCustomHeadersSchema(rowIndex: number): import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null {
    return this.activeVersions()[rowIndex]?.custom_llm_headers_open_api_schema ?? null;
  }
  getLlmRowCustomBodySchema(rowIndex: number): import('@generated/models/open-api-properties-schema').OpenApiPropertiesSchema | null {
    return this.activeVersions()[rowIndex]?.custom_llm_body_open_api_schema ?? null;
  }

  onSttModelSelected(versionId: string): void {
    if (versionId && this.agent()) {
      const previousVersionId = this.sttEntry()?.model_version_id;
      // Меняем модель — свойства прежней схемы больше не применимы
      this.upsertSingletonEntry('stt', {
        model_version_id: versionId,
        ...(previousVersionId !== versionId ? { additional_properties: [] } : {}),
      });
      this.selectedSttModelVersion = this.findSttNodeByVersionId(versionId);
      this.bumpAgentSignal();
      this.cdr.markForCheck();
    }
  }

  onTtsModelSelected(agentLocale: AgentLanguageLocaleDto, versionId: string): void {
    if (!agentLocale.id) return;
    const existing = this.ttsEntryForLocale(agentLocale.id);
    if (versionId && existing?.model_version_id !== versionId) {
      // Смена модели — голос от прежней версии сбрасываем.
      this.upsertTtsForLocale(agentLocale.id, {
        model_version_id: versionId,
        voice_id: null,
      });
      this.bumpAgentSignal();
      this.cdr.markForCheck();
    }
  }

  /**
   * "Recommended only" / "All" toggle from any of the model-selector
   * buttons — refetch the hierarchy with the new includeAll flag and
   * rebuild the dependent tree-node caches so subsequent selections
   * see the new catalog.
   */
  onModelsIncludeAllChange(includeAll: boolean): void {
    this.modelsIncludeAll = includeAll;
    this.modelsHierarchyLoading.set(true);
    this.cdr.markForCheck();
    this.modelsService.apiModelsHierarchyGet({ includeAll })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (hierarchy) => {
          this.modelsHierarchy = hierarchy;
          this.buildLlmTreeNodes();
          this.buildTtsTreeNodes();
          this.buildSttTreeNodes();
          this.modelsHierarchyLoading.set(false);
          this.cdr.markForCheck();
        },
        error: () => {
          this.modelsHierarchyLoading.set(false);
          this.cdr.markForCheck();
          this.notificationService.showRetriableError(
            $localize`:@@agentEdit.models.refreshFailed:Failed to refresh models list`,
            () => this.onModelsIncludeAllChange(includeAll),
            {
              title: $localize`:@@common.error:Error`,
              retryLabel: $localize`:@@common.retry:Retry`,
            },
          );
        }
      });
  }

  // Legacy onLlmTesterSelected / onLlmFallbackTesterSelected удалены —
  // HTML использует единый onActiveTesterSelected (LLM + Realtime).

  onSttTesterSelected(selection: TesterSelection): void {
    if (!this.agent()) return;
    this.upsertSingletonEntry('stt', {
      model_version_id: selection.versionId,
      additional_properties: selection.additionalProperties
        ? dictToValueEntries(selection.additionalProperties)
        : [],
    });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  onVadModelSelected(versionId: string): void {
    if (versionId && this.agent()) {
      const previousVersionId = this.vadEntry()?.model_version_id;
      // Свежая версия — свойства из старой схемы больше не применимы;
      // properties-edit подхватит defaults из новой schema.
      this.upsertSingletonEntry('vad', {
        model_version_id: versionId,
        ...(previousVersionId !== versionId ? { additional_properties: [] } : {}),
      });
      this.bumpAgentSignal();
      this.cdr.markForCheck();
    }
  }

  onVadTesterSelected(selection: TesterSelection): void {
    if (!this.agent()) return;
    this.upsertSingletonEntry('vad', {
      model_version_id: selection.versionId,
      additional_properties: selection.additionalProperties
        ? dictToValueEntries(selection.additionalProperties)
        : [],
    });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  onTtsTesterSelected(agentLocale: AgentLanguageLocaleDto, selection: TesterSelection): void {
    if (!agentLocale.id) return;
    this.upsertTtsForLocale(agentLocale.id, {
      model_version_id: selection.versionId,
      voice_id: selection.voiceId,
      additional_properties: selection.additionalProperties
        ? dictToValueEntries(selection.additionalProperties)
        : [],
    });
    this.bumpAgentSignal();
    this.cdr.markForCheck();
  }

  // ==== Agent Mode (Classic / Realtime) — signal-based ====
  /**
   * Classic — обычный pipeline STT→LLM→TTS с независимыми моделями.
   * Realtime — единая voice-to-voice модель. Модель одна на весь агент
   * (хранится в agent.realtime_versions[]), голос по-прежнему per-locale
   * (locale.voice_id). LLM-блок в UI автоматически переключается на
   * Realtime-модель через `activeVersions()` computed'ы.
   */
  readonly agentModeOptions = [
    { label: $localize`:@@agentEdit.mode.classic:Classic`, value: 'Classic' },
    { label: $localize`:@@agentEdit.mode.realtime:Realtime`, value: 'Realtime' },
  ];

  readonly isRealtimeMode = computed(() => this.agent()?.mode === 'Realtime');

  /** modelTypes prop для <app-model-selector-button> в верхнем model-picker'е —
   *  Cascade → LLM, Realtime → Realtime модели. */
  readonly activeModelTypes = computed<string[]>(() =>
    this.isRealtimeMode() ? ['realtime'] : ['llm'],
  );

  /** Активный каскад версий — фильтруется из плоского
   *  agent.model_versions[] по типу entry (llm или realtime,
   *  в зависимости от mode). Пустые ряды тоже в списке — у них
   *  snapshot-stub с проставленным типом (см. addActiveRow /
   *  ensureActivePrimary). sort_index задаёт порядок перебора. */
  readonly activeVersions = computed<AgentModelVersionEntryDto[]>(() => {
    if (!this.agent()) return [];
    return this.isRealtimeMode() ? this.realtimeEntries() : this.llmEntries();
  });

  readonly activePrimary = computed<AgentModelVersionEntryDto | undefined>(
    () => this.activeVersions()[0],
  );
  readonly activePrimaryVersionId = computed(() => this.activePrimary()?.model_version_id ?? '');
  readonly activePrimarySnapshot = computed<AgentModelVersionDto | null | undefined>(
    () => this.activePrimary()?.model_version,
  );
  readonly activePrimaryTemperature = computed(() => this.activePrimary()?.temperature ?? null);
  readonly activePrimaryTopP = computed(() => this.activePrimary()?.top_p ?? null);
  readonly activePrimaryTopK = computed(() => this.activePrimary()?.top_k ?? null);
  readonly activePrimaryMaxOutputTokens = computed(() => this.activePrimary()?.max_output_tokens ?? null);
  readonly activePrimaryVoiceId = computed(() => this.activePrimary()?.voice_id ?? null);

  /** Ряды каскада для рендера. У агента без единой LLM-entry цепочка
   *  пуста — показываем один пустой ряд, чтобы человеку было куда
   *  выбирать модель. Материализуется он лениво, при первом выборе
   *  (ensureActivePrimary внутри activeRowSlot), так что индексы рядов
   *  совпадают с индексами в activeVersions(). */
  readonly activeCascadeRows = computed<AgentModelVersionEntryDto[]>(() => {
    const rows = this.activeVersions();
    if (rows.length > 0) return rows;
    return [{
      model_version_id: '',
      sort_index: 0,
      additional_properties: [],
      temperature: null,
      model_version: this.makeTypeStub(this.activeCode()),
    }];
  });

  /** Id всех выбранных в каскаде версий — база для excludeVersionIds
   *  (одну и ту же модель нельзя поставить в каскад дважды). */
  readonly activeRowIds = computed(() =>
    this.activeVersions().map((r) => r.model_version_id ?? ''),
  );
  readonly canAddActiveRow = computed(
    () => this.activeVersions().length < this.MAX_LLM_CASCADE,
  );
  /** Последний ряд не удаляем — агенту нужна хотя бы одна модель. */
  readonly canRemoveActiveRow = computed(() => this.activeVersions().length > 1);

  /** Realtime режим: tester выбирает только голос. Модель одна на весь
   *  агент (единственная realtime entry в model_versions[]); voice_id
   *  живёт прямо в ней, локаль здесь ни причём. */
  onRealtimeVoiceTesterSelected(_locale: AgentLanguageLocaleDto, selection: TesterSelection): void {
    if (!this.agent()) return;
    const chain = this.cloneActiveChain();
    if (chain.length === 0) return;
    chain[0].voice_id = selection.voiceId ?? null;
    this.replaceChain('realtime', chain);
    this.bumpAgentSignal();
  }

  /**
   * Номер для звонка оператору вне телефонного разговора. Поле агентское, а
   * редактируется в настройках инструмента — поэтому значение приходит сюда
   * событием и кладётся в агента, откуда его подхватит общий save.
   */
  onDefaultPhoneNumberChange(phoneNumberAgentId: string | null): void {
    const current = this.agent();
    if (!current) return;
    this.agent.set({ ...current, default_phone_number_agent_id: phoneNumberAgentId });
  }

  onAgentModeChange(mode: AgentMode | null): void {
    if (!this.agent() || !mode) return;
    this.agent()!.mode = mode;
    this.agent.set({ ...this.agent()! });
    this.cdr.markForCheck();
    // PrimeNG p-tabs пересчитывает InkBar через effect на изменение
    // [value]. При смене режима value не меняется — active tab остаётся
    // тем же — а список <p-tab> и их x-координаты да. Форсим пересчёт
    // руками после того как @if успел перерисовать DOM.
    // PrimeNG p-tabs пересчитывает InkBar только через effect на изменение
    // [value]. При смене режима value то же — а набор <p-tab> и их
    // координаты меняются. Ждём следующего рендера и меряем чисто.
    afterNextRender(() => this.agentTabListRef?.updateInkBar(), { injector: this.injector });
  }

  // Мутации активной цепочки — работают с копией chain и через
  // replaceChain кладут её обратно в плоский agent.model_versions[].
  private activeCode(): 'llm' | 'realtime' {
    return this.isRealtimeMode() ? 'realtime' : 'llm';
  }

  private cloneActiveChain(): AgentModelVersionEntryDto[] {
    return this.activeVersions().map(e => ({ ...e }));
  }

  private ensureActivePrimary(chain: AgentModelVersionEntryDto[]): AgentModelVersionEntryDto {
    if (chain.length === 0) {
      chain.push({
        model_version_id: '',
        sort_index: 0,
        additional_properties: [],
        temperature: null,
        model_version: this.makeTypeStub(this.activeCode()),
      });
    }
    chain[0].sort_index = 0;
    return chain[0];
  }

  /** Все уже занятые в каскаде версии, кроме собственной версии ряда. */
  activeRowExcludeIds(rowIndex: number): string[] {
    return this.activeRowIds().filter((id, i) => !!id && i !== rowIndex);
  }

  getActiveRowSavedModel(rowIndex: number): AgentModelVersionDto | null | undefined {
    return this.activeVersions()[rowIndex]?.model_version ?? null;
  }

  getActiveRowAdditionalValuesDict(rowIndex: number): { [key: string]: any } {
    return valueEntriesToDict(this.activeVersions()[rowIndex]?.additional_properties);
  }

  getActiveRowTemperature(rowIndex: number): number | null {
    return this.activeVersions()[rowIndex]?.temperature ?? null;
  }
  getActiveRowTopP(rowIndex: number): number | null {
    return this.activeVersions()[rowIndex]?.top_p ?? null;
  }
  getActiveRowTopK(rowIndex: number): number | null {
    return this.activeVersions()[rowIndex]?.top_k ?? null;
  }
  getActiveRowMaxOutputTokens(rowIndex: number): number | null {
    return this.activeVersions()[rowIndex]?.max_output_tokens ?? null;
  }

  private bumpAgentSignal(): void {
    const a = this.agent();
    if (!a) return;
    // Пересобираем model_versions с новым ref, чтобы downstream computed'ы
    // (activeVersions, activeRowIds, canAddActiveRow, template
    // @for) не залипали на старой ссылке при in-place мутациях.
    if (a.model_versions) a.model_versions = [...a.model_versions];
    this.agent.set({ ...a });
  }

  /** Ряд каскада по абсолютному индексу. Нулевой ряд материализуем, если
   *  цепочка пуста — остальные обязаны уже существовать (их создаёт
   *  addActiveRow). */
  private activeRowSlot(
    chain: AgentModelVersionEntryDto[],
    rowIndex: number,
  ): AgentModelVersionEntryDto | null {
    if (rowIndex === 0) return this.ensureActivePrimary(chain);
    return chain[rowIndex] ?? null;
  }

  onActiveRowModelSelected(rowIndex: number, versionId: string): void {
    if (!versionId || !this.agent()) return;
    const chain = this.cloneActiveChain();
    const slot = this.activeRowSlot(chain, rowIndex);
    if (!slot) return;
    slot.model_version_id = versionId;
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
  }

  onActiveRowTesterSelected(rowIndex: number, selection: TesterSelection): void {
    if (!this.agent()) return;
    const chain = this.cloneActiveChain();
    const slot = this.activeRowSlot(chain, rowIndex);
    if (!slot) return;
    slot.model_version_id = selection.versionId;
    slot.additional_properties = selection.additionalProperties
      ? dictToValueEntries(selection.additionalProperties)
      : [];
    slot.temperature = selection.temperature;
    slot.top_p = selection.topP;
    slot.top_k = selection.topK;
    slot.max_output_tokens = selection.maxOutputTokens;
    // voice_id — Realtime voice-to-voice модель хранит голос в самой
    // модели; для LLM тестер возвращает null.
    slot.voice_id = selection.voiceId ?? null;
    if (!this.isRealtimeMode()) {
      slot.custom_llm_headers_open_api_schema = selection.customHeadersSchema;
      slot.custom_llm_body_open_api_schema = selection.customBodySchema;
    }
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
  }

  addActiveRow(): void {
    if (!this.agent() || !this.canAddActiveRow()) return;
    const chain = this.cloneActiveChain();
    if (chain.length === 0) this.ensureActivePrimary(chain);
    // Пустая entry получает snapshot-stub с текущим типом (llm или
    // realtime) — иначе фильтр по type её теряет и ряд не рендерится.
    chain.push({
      model_version_id: '',
      sort_index: chain.length,
      additional_properties: [],
      temperature: null,
      model_version: this.makeTypeStub(this.activeCode()),
    });
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
  }

  removeActiveRow(rowIndex: number): void {
    if (!this.agent() || !this.canRemoveActiveRow()) return;
    const chain = this.cloneActiveChain();
    if (rowIndex < 0 || rowIndex >= chain.length) return;
    chain.splice(rowIndex, 1);
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
  }

  activeRowDragIndex: number | null = null;

  onActiveRowDragStart(rowIndex: number): void { this.activeRowDragIndex = rowIndex; }
  onActiveRowDragEnd(): void { this.activeRowDragIndex = null; }

  onActiveRowDrop(targetRowIndex: number): void {
    const src = this.activeRowDragIndex;
    this.activeRowDragIndex = null;
    if (src == null || src === targetRowIndex || !this.agent()) return;
    const chain = this.cloneActiveChain();
    if (src < 0 || src >= chain.length) return;
    const [moved] = chain.splice(src, 1);
    chain.splice(targetRowIndex, 0, moved);
    this.replaceChain(this.activeCode(), chain);
    this.bumpAgentSignal();
  }

}